import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getStoredLanguage } from '@/services/userPreferencesService';

// Initialize with stored language preference
const initialLanguage = getStoredLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: 'en',
    debug: false, // Disabled for production to prevent console clutter

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },

    defaultNS: 'common',
    ns: ['common'],

    resources: {
      en: {
        common: {
          // Navigation
          'nav.home': 'Home',
          'nav.features': 'Features',
          'nav.howItWorks': 'How It Works',
          'nav.help': 'Help',
          'nav.createPoll': 'Create Poll',
          'nav.login': 'Login',
          'nav.signup': 'Sign Up',
          'nav.logout': 'Logout',
          'nav.profile': 'Profile',
          'nav.dashboard': 'Dashboard',
          'nav.polls': 'Polls',
          'nav.analytics': 'Analytics',
          'nav.settings': 'Settings',
          'nav.language': 'Language',
          'nav.theme': 'Theme',
          'nav.light': 'Light',
          'nav.dark': 'Dark',

          // Common actions
          'common.save': 'Save',
          'common.cancel': 'Cancel',
          'common.delete': 'Delete',
          'common.edit': 'Edit',
          'common.create': 'Create',
          'common.submit': 'Submit',
          'common.loading': 'Loading...',
          'common.error': 'Error',
          'common.success': 'Success',
          'common.close': 'Close',
          'common.back': 'Back',
          'common.next': 'Next',
          'common.previous': 'Previous',
          'common.continue': 'Continue',
          'common.finish': 'Finish',
          'common.dismiss': 'Dismiss',
          'common.saving': 'Saving...',
          'common.user': 'User',

          // Authentication
          'auth.login': 'Login',
          'auth.signup': 'Sign Up',
          'auth.logout': 'Logout',
          'auth.email': 'Email',
          'auth.password': 'Password',
          'auth.confirmPassword': 'Confirm Password',
          'auth.forgotPassword': 'Forgot Password?',
          'auth.resetPassword': 'Reset Password',
          'auth.signInWithGoogle': 'Sign in with Google',
          'auth.createAccount': 'Create Account',
          'auth.alreadyHaveAccount': 'Already have an account?',
          'auth.dontHaveAccount': "Don't have an account?",
          'auth.loginSuccess': 'Successfully logged in!',
          'auth.logoutSuccess': 'Successfully logged out!',
          'auth.signupSuccess': 'Account created successfully!',
          'auth.invalidCredentials': 'Invalid email or password',
          'auth.emailRequired': 'Email is required',
          'auth.passwordRequired': 'Password is required',
          'auth.passwordTooShort': 'Password must be at least 6 characters',
          'auth.passwordsDoNotMatch': 'Passwords do not match',
          'auth.emailInvalid': 'Please enter a valid email address',
          'auth.fullName': 'Full Name',
          'auth.fullNameRequired': 'Full name is required',
          'auth.fullNameTooShort': 'Name must be at least 2 characters',
          'auth.confirmPasswordRequired': 'Please confirm your password',
          'auth.welcomeBack': 'Welcome back to Meet',
          'auth.joinMeet': 'Join Meet and start scheduling smarter',
          'auth.orContinueWith': 'Or continue with',
          'auth.emailAlreadyInUse': 'Email is already in use',
          'auth.createEmailAccount': 'Create Email Account',
          'auth.signInWithGoogleAccount': 'Sign in with Google',
          'auth.accountCreationOptions': 'Create an account to save your data permanently',
          'auth.passwordStrength': 'Password Strength',
          'auth.passwordWeak': 'Weak',
          'auth.passwordMedium': 'Medium',
          'auth.passwordStrong': 'Strong',
          'auth.dataMigrationSuccess': 'Your data has been successfully transferred to your new account!',
          'auth.dataMigrationError': 'There was an error transferring your data. Please try again.',
          'auth.passwordPlaceholder': 'Enter your password',
          'auth.confirmPasswordPlaceholder': 'Confirm your password',

          // Settings
          'settings.title': 'Settings',
          'settings.subtitle': 'Manage your preferences and account settings',
          'settings.timezone': 'Timezone',
          'settings.timezoneDesc': 'Override your browser timezone for polls',
          'settings.calendarStartDay': 'Calendar Start Day',
          'settings.calendarStartDayDesc': 'Choose which day your calendar week starts on',
          'settings.monday': 'Monday',
          'settings.sunday': 'Sunday',
          'settings.useSystemTimezone': 'Use system timezone',
          'settings.saveSettings': 'Save Settings',
          'settings.settingsUpdated': 'Settings updated successfully!',
          'settings.settingsError': 'Failed to update settings',

          // Poll creation
          'poll.create': 'Create Poll',
          'poll.title': 'Poll Title',
          'poll.description': 'Description',
          'poll.timeSlots': 'Time Slots',
          'poll.addTimeSlot': 'Add Time Slot',
          'poll.removeTimeSlot': 'Remove Time Slot',
          'poll.settings': 'Settings',
          'poll.allowMultipleVotes': 'Allow multiple votes per person',
          'poll.deadline': 'Voting Deadline',
          'poll.deadlinePlaceholder': 'Select voting deadline (optional)',

          'poll.createSuccess': 'Poll created successfully!',
          'poll.createError': 'Failed to create poll',
          'poll.titleRequired': 'Poll title is required',
          'poll.timeSlotsRequired': 'At least one time slot is required',
          'poll.selectTimeSlots': 'Select available time slots',
          'poll.view': 'View',
          'poll.viewPoll': 'View Poll',
          'poll.copyLink': 'Copy Link',
          'poll.goToDashboard': 'Go to Dashboard',
          'poll.createAnother': 'Create Another Poll',
          'poll.successMessage': 'Your poll has been created and is ready to share!',
          'poll.reset': 'Reset',
          'poll.titlePlaceholder': 'Enter poll title',
          'poll.descriptionPlaceholder': 'Optional description for your poll',
          'poll.noTimeSlots': 'No time slots added yet',
          'poll.noTimeSlotsDesc': 'Click "Add Time Slot" to get started',

          // Poll voting
          'vote.cast': 'Cast Vote',
          'vote.update': 'Update Vote',
          'vote.remove': 'Remove Vote',

          'vote.yourName': 'Your Name',
          'vote.selectOptions': 'Select your available times',
          'vote.voteSuccess': 'Vote submitted successfully!',
          'vote.voteError': 'Failed to submit vote',
          'vote.nameRequired': 'Name is required',
          'vote.optionsRequired': 'Please select at least one option',
          'vote.participants': 'participants',
          'vote.yourVote': 'Your Vote',
          'vote.multipleAllowed': 'You can select multiple time slots',
          'vote.singleOnly': 'You can select only one time slot',
          'vote.edit': 'Edit Vote',
          'vote.submitted': 'Vote Submitted',
          'vote.justSubmitted': 'Vote Just Submitted!',
          'vote.submittedAs': 'Voted as',
          'vote.namePlaceholder': 'Enter your name',
          'vote.updateSuccess': 'Vote updated successfully!',
          'vote.deleteSuccess': 'Vote removed successfully!',
          'vote.deleteError': 'Failed to remove vote',
          'vote.autoSavedFromEmail': 'Your vote for {{slot}} has been automatically saved!',
          'vote.autoSaveError': 'Failed to automatically save your vote',
          'vote.vote': 'vote',
          'vote.votes': 'votes',
          'vote.matrixTitle': 'Vote on Available Time Slots',
          'vote.you': 'You',
          'vote.anonymous': 'Anonymous',
          'vote.anonymousUser': 'You (anonymous)',
          'vote.editName': 'Edit name',
          'vote.changeAvatar': 'Change avatar',
          'vote.enterName': 'Enter your name',
          'vote.yes': 'Yes',
          'vote.maybe': 'Maybe',
          'vote.no': 'No',

          // Poll results
          'results.title': 'Poll Results',
          'results.noVotes': 'No votes yet',
          'results.noVotesDesc': 'Be the first to vote on this poll!',
          'results.totalVotes': 'Total Votes',
          'results.mostPopular': 'Most Popular',
          'results.exportCalendar': 'Export to Calendar',
          'results.sharePoll': 'Share Poll',
          'results.closePoll': 'Close Poll',
          'results.reopenPoll': 'Reopen Poll',
          'results.liveUpdates': 'Results update in real-time',
          'results.export': 'Export',
          'results.share': 'Share',
          'results.timeSlots': 'Time Slots',
          'results.voteDistribution': 'Vote Distribution',
          'results.votePercentages': 'Vote Percentages',
          'results.detailedResults': 'Detailed Results',
          'results.timeSlot': 'Time Slot',
          'results.votes': 'Votes',
          'results.percentage': 'Percentage',
          'results.voters': 'Voters',
          'results.noVoters': 'No votes',
          'results.winningTimeSlot': 'Most Popular Time Slot',

          // Poll management
          'poll.notFound': 'Poll Not Found',
          'poll.notFoundDesc': 'The poll you are looking for does not exist or has been removed.',
          'poll.loadError': 'Failed to load poll',
          'poll.createdBy': 'Created by',
          'poll.closed': 'Voting Closed',
          'poll.votingClosed': 'Voting for this poll has been closed',
          'poll.share': 'Share Poll',
          'poll.linkCopied': 'Poll link copied to clipboard!',
          'poll.copyError': 'Failed to copy link',
          'poll.availableTimeSlots': 'Available Time Slots',
          'poll.edit': 'Edit Poll',
          'poll.editSuccess': 'Poll updated successfully!',
          'poll.editError': 'Failed to update poll',
          'poll.editPoll': 'Edit Poll',
          'poll.updatePoll': 'Update Poll',
          'poll.editDescription': 'Make changes to your poll',

          // Email embedding
          'poll.embedInEmail': 'Embed in Email',
          'poll.emailEmbed': 'Email Embed',
          'poll.emailEmbedTitle': 'Embed Poll in Email',
          'poll.emailEmbedDescription': 'Generate email-compatible HTML that you can copy and paste into your emails. Recipients can vote directly by clicking on time slots.',
          'poll.emailEmbedGenerate': 'Generate Email HTML',
          'poll.emailEmbedCopy': 'Copy HTML',
          'poll.emailEmbedCopied': 'Email HTML copied to clipboard!',
          'poll.emailEmbedCopyError': 'Failed to copy HTML',
          'poll.emailEmbedCopyRendered': 'Copy for Email',
          'poll.emailEmbedRenderedCopied': 'Email content copied to clipboard!',
          'poll.emailEmbedPreview': 'Preview',
          'poll.emailEmbedInstructions': 'Use the "Copy for Email" button above for best results, or copy the HTML source code below.',
          'poll.emailEmbedCompatibility': 'Compatible with Gmail, Outlook, Apple Mail, and most email clients.',
          'poll.emailEmbedHtmlStyle': 'HTML Style',
          'poll.emailEmbedRich': 'Rich (Recommended)',
          'poll.emailEmbedSimple': 'Simple (High Compatibility)',
          'poll.emailEmbedHtmlSourceCode': 'HTML Source Code',
          'poll.emailEmbedInstructionsTitle': 'Instructions',
          'poll.emailEmbedOption1Title': 'Option 1 (Recommended):',
          'poll.emailEmbedOption1Description': 'Use the "Copy for Email" button in the preview above. This works best with Gmail and most modern email clients.',
          'poll.emailEmbedOption2Title': 'Option 2:',
          'poll.emailEmbedOption2Description': 'Copy the HTML source code below and paste it into your email\'s HTML editor.',
          'poll.emailEmbedRecipientsNote': 'Recipients can click on time slots to vote directly from the email.',
          'poll.preSelectedSlot': 'Pre-selected from email',
          'poll.createSubtitle': 'Create a new poll to find the perfect meeting time for your team',
          'poll.authError': 'Failed to authenticate user',
          'poll.allowMultipleVotesDesc': 'Allow participants to select multiple time slots',

          // Poll closure settings
          'poll.autoCloseSettings': 'Auto-Close Settings',
          'poll.autoCloseExplanation': 'The poll will automatically close when the first condition is met.',
          'poll.enableDeadlineClose': 'Close by Deadline',
          'poll.enableDeadlineCloseDesc': 'Automatically close poll when the deadline is reached',
          'poll.enableParticipantLimitClose': 'Close by Participant Limit',
          'poll.enableParticipantLimitCloseDesc': 'Automatically close poll when maximum participants is reached',
          'poll.maxParticipants': 'Maximum Participants',
          'poll.maxParticipantsDesc': 'Automatically close poll when this many people have voted',
          'poll.maxParticipantsPlaceholder': 'e.g. 2',
          'poll.enableMajorityClose': 'Close by Majority Threshold',
          'poll.enableMajorityCloseDesc': 'Close poll when any time slot gets majority votes',
          'poll.expectedParticipants': 'Expected Total Participants',
          'poll.expectedParticipantsDesc': 'Total number of people expected to vote (for majority calculation)',
          'poll.expectedParticipantsPlaceholder': 'e.g. 2',

          // Poll closure
          'poll.closePoll': 'Close Poll',
          'poll.closePollConfirm': 'Are you sure you want to close this poll?',
          'poll.closePollDesc': 'This will prevent further voting and select a winning time slot.',
          'poll.selectWinningSlot': 'Select Winning Time Slot',
          'poll.autoSelectWinner': 'Auto-select most popular',
          'poll.manualSelectWinner': 'Manually select winner',
          'poll.confirmClose': 'Confirm Close',
          'poll.pollClosed': 'Poll Closed',
          'poll.closedBy': 'Closed by',
          'poll.closedReason': 'Reason',
          'poll.closedReasonManual': 'Manually closed',
          'poll.closedReasonDeadline': 'Deadline reached',
          'poll.closedReasonParticipantLimit': 'Participant limit reached',
          'poll.closedReasonMajority': 'Majority threshold reached',
          'poll.winningTimeSlot': 'Winning Time Slot',
          'poll.reopenPoll': 'Reopen Poll',
          'poll.activePolls': 'Active Polls',


          // Home page
          'home.title': 'Schedule Meetings Effortlessly',
          'home.subtitle': 'Join the dots. Meet at the perfect time.',
          'home.description': 'Create polls, coordinate with your team, and find the perfect meeting time effortlessly.',
          'home.getStarted': 'Get Started',
          'home.learnMore': 'Learn More',
          'home.features.title': 'Powerful Features',
          'home.features.smartTimezone': 'Smart Timezone Handling',
          'home.features.smartTimezoneDesc': 'Automatically converts times to participants\' local timezones',
          'home.features.anonymous': 'Anonymous Voting',
          'home.features.anonymousDesc': 'Allow participants to vote without creating an account',
          'home.features.realtime': 'Real-time Updates',
          'home.features.realtimeDesc': 'See vote results update instantly as people respond',
          'home.features.calendar': 'Calendar Integration',
          'home.features.calendarDesc': 'Export final meeting times directly to your calendar',
          'home.features.responsive': 'Mobile Friendly',
          'home.features.responsiveDesc': 'Works perfectly on all devices and screen sizes',
          'home.features.analytics': 'Smart Analytics',
          'home.features.analyticsDesc': 'Get insights into participation patterns and optimal meeting times',
          'home.features.subtitle': 'Everything you need to schedule meetings efficiently and effortlessly.',
          'home.features.allFeatures': 'All Features',
          'home.cta.title': 'Ready to streamline your meetings?',
          'home.cta.subtitle': 'Join thousands of teams who use Meet to coordinate their schedules effortlessly.',
          'home.cta.createFirstPoll': 'Create Your First Poll',

          // Meet section
          'home.meet.title': 'Join the dots to meet at the perfect time',
          'home.meet.stats.polls': 'Polls Created',
          'home.meet.stats.participants': 'Happy Participants',
          'home.meet.stats.satisfaction': 'Satisfaction Rate',

          // How It Works section
          'home.howItWorks.title': 'How It Works',
          'home.howItWorks.subtitle': 'Simple steps to coordinate perfect meetings',
          'home.howItWorks.step': 'Step',
          'home.howItWorks.step1.title': 'Create Your Poll',
          'home.howItWorks.step1.description': 'Set up your meeting details and suggest time slots using our intuitive calendar interface.',
          'home.howItWorks.step2.title': 'Share the Link',
          'home.howItWorks.step2.description': 'Send the poll link to participants via email, chat, or any messaging platform.',
          'home.howItWorks.step3.title': 'Collect Votes',
          'home.howItWorks.step3.description': 'Participants vote on their availability with simple yes/maybe/no options.',
          'home.howItWorks.step4.title': 'Review Results',

          // Testimonials section
          'home.testimonials.title': 'What Our Users Say',
          'home.testimonials.subtitle': 'Join thousands of satisfied teams worldwide',
          'home.testimonials.testimonial1.text': 'Meet has revolutionized how we schedule team meetings. No more endless email chains!',
          'home.testimonials.testimonial1.author': 'James Wilson',
          'home.testimonials.testimonial1.role': 'Project Manager',
          'home.testimonials.testimonial2.text': 'The timezone handling is incredible. Our global team can finally coordinate effortlessly.',
          'home.testimonials.testimonial2.author': 'Michael Chen',
          'home.testimonials.testimonial2.role': 'Engineering Lead',
          'home.testimonials.testimonial3.text': 'Simple, intuitive, and powerful. Meet saves us hours every week.',
          'home.testimonials.testimonial3.author': 'Emma Rodriguez',
          'home.testimonials.testimonial3.role': 'Operations Director',
          'home.testimonials.testimonial4.text': 'Anonymous voting feature is perfect for our client meetings. Highly recommended!',
          'home.testimonials.testimonial4.author': 'Lisa Park',
          'home.testimonials.testimonial4.role': 'Consultant',
          'home.testimonials.testimonial5.text': 'The best scheduling tool we\'ve ever used. Clean interface and great features.',
          'home.testimonials.testimonial5.author': 'David Thompson',
          'home.testimonials.testimonial5.role': 'Marketing Manager',
          'home.testimonials.testimonial6.text': 'Meet makes coordinating with external partners so much easier. Love the simplicity!',
          'home.testimonials.testimonial6.author': 'Sarah Johnson',
          'home.testimonials.testimonial6.role': 'Business Development',
          'home.howItWorks.step4.description': 'See the optimal time slots and finalize your meeting with the best attendance.',
          'home.howItWorks.interactive.create': 'Click to create poll',
          'home.howItWorks.interactive.share': 'Copy link to share',
          'home.howItWorks.interactive.vote': 'Vote on time slots',
          'home.howItWorks.interactive.results': 'View optimal times',

          // How It Works - Demo Content
          'home.howItWorks.demo.teamMeetingPoll': 'Team Meeting Poll',
          'home.howItWorks.demo.pollTitle': 'Poll Title',
          'home.howItWorks.demo.weeklyTeamSync': 'Weekly Team Sync',
          'home.howItWorks.demo.selectedTimeSlots': 'Selected Time Slots',
          'home.howItWorks.demo.monday': 'Monday',
          'home.howItWorks.demo.wednesday': 'Wednesday',
          'home.howItWorks.demo.friday': 'Friday',
          'home.howItWorks.demo.dec16': 'Dec 16',
          'home.howItWorks.demo.dec18': 'Dec 18',
          'home.howItWorks.demo.dec20': 'Dec 20',
          'home.howItWorks.demo.time1': '2:00 PM - 3:00 PM',
          'home.howItWorks.demo.time2': '10:00 AM - 11:00 AM',
          'home.howItWorks.demo.time3': '3:00 PM - 4:00 PM',
          'home.howItWorks.demo.createPoll': 'Create Poll',
          'home.howItWorks.demo.sharePoll': 'Share Poll',
          'home.howItWorks.demo.pollCreatedSuccess': 'Poll created successfully!',
          'home.howItWorks.demo.shareLink': 'Share this link with participants',
          'home.howItWorks.demo.quickShareOptions': 'Quick share options',
          'home.howItWorks.demo.email': 'Email',
          'home.howItWorks.demo.chat': 'Chat',
          'home.howItWorks.demo.pollId': 'Poll ID: abc123xyz',
          'home.howItWorks.demo.timeSlotsAvailable': '3 time slots available',
          'home.howItWorks.demo.anonymousVotingEnabled': 'Anonymous voting enabled',
          'home.howItWorks.demo.votingMatrix': 'Voting Matrix',
          'home.howItWorks.demo.participant': 'Participant',
          'home.howItWorks.demo.mon2pm': 'Mon 2PM',
          'home.howItWorks.demo.wed10am': 'Wed 10AM',
          'home.howItWorks.demo.fri3pm': 'Fri 3PM',
          'home.howItWorks.demo.user1': 'Daniel Johnson',
          'home.howItWorks.demo.user2': 'Samuel Smith',
          'home.howItWorks.demo.user3': 'Joe Davis',
          'home.howItWorks.demo.you': 'You (Vanessa)',
          'home.howItWorks.demo.yes': 'Yes',
          'home.howItWorks.demo.maybe': 'Maybe',
          'home.howItWorks.demo.no': 'No',
          'home.howItWorks.demo.pollResults': 'Poll Results',
          'home.howItWorks.demo.bestOption': 'Best Option',
          'home.howItWorks.demo.mondayDec16': 'Monday, Dec 16',
          'home.howItWorks.demo.participantsAvailable': '4/4 participants available',
          'home.howItWorks.demo.allOptions': 'All Options',
          'home.howItWorks.demo.monday2pm': 'Monday 2:00 PM',
          'home.howItWorks.demo.wednesday10am': 'Wednesday 10:00 AM',
          'home.howItWorks.demo.friday3pm': 'Friday 3:00 PM',
          'home.howItWorks.demo.best': 'Best',
          'home.howItWorks.demo.finalizeMeeting': 'Finalize Meeting',

          // Highlighted Features section
          'home.highlightedFeatures.title': 'Powerful Features',
          'home.highlightedFeatures.subtitle': 'Discover what makes our polling platform the perfect choice for your team',
          'home.highlightedFeatures.learnMore': 'Learn more',
          'home.highlightedFeatures.anonymousCreation.title': 'Anonymous Poll Creation',
          'home.highlightedFeatures.anonymousCreation.description': 'Create polls instantly without any login required. Start coordinating meetings in seconds, no barriers or sign-up friction.',
          'home.highlightedFeatures.anonymousVoting.title': 'Anonymous Voting',
          'home.highlightedFeatures.anonymousVoting.description': 'Participants can vote on their availability without creating accounts. Simple, fast, and privacy-focused voting experience.',
          'home.highlightedFeatures.analytics.title': 'Powerful Analytics',
          'home.highlightedFeatures.analytics.description': 'Get detailed insights into your polls with comprehensive analytics, charts, and KPIs to make data-driven scheduling decisions.',
          'home.highlightedFeatures.emailEmbedding.title': 'Email Embedding',
          'home.highlightedFeatures.emailEmbedding.description': 'Embed polls directly in emails with HTML tables and direct voting links. Let participants vote without leaving their inbox.',

          // Error messages
          'error.generic': 'Something went wrong. Please try again.',
          'error.network': 'Network error. Please check your connection.',
          'error.unauthorized': 'You are not authorized to perform this action.',
          'error.notFound': 'The requested resource was not found.',
          'error.serverError': 'Server error. Please try again later.',
          'error.tryAgain': 'Try Again',

          // Success messages
          'success.saved': 'Saved successfully!',
          'success.deleted': 'Deleted successfully!',
          'success.updated': 'Updated successfully!',
          'success.created': 'Created successfully!',
          'success.sent': 'Sent successfully!',

          // Time and dates
          'time.today': 'Today',
          'time.tomorrow': 'Tomorrow',
          'time.yesterday': 'Yesterday',
          'time.thisWeek': 'This Week',
          'time.nextWeek': 'Next Week',
          'time.thisMonth': 'This Month',
          'time.nextMonth': 'Next Month',
          'time.selectDate': 'Select Date',
          'time.selectTime': 'Select Time',
          'time.duration': 'Duration',
          'time.timezone': 'Timezone',
          'time.slotDuration': 'Slot Duration',
          'time.30minutes': '30 minutes',
          'time.1hour': '1 hour',
          'time.2hours': '2 hours',
          'time.custom': 'Custom',
          'time.customDuration': 'Custom Duration',
          'time.minutes': 'minutes',
          'time.hours': 'hours',
          'time.unit': 'Unit',
          'time.selectDuration': 'Select duration for time slots',
          'time.timezoneInfo': 'Times are shown in your local timezone',

          // Days
          'days.short.monday': 'MON',
          'days.short.tuesday': 'TUE',
          'days.short.wednesday': 'WED',
          'days.short.thursday': 'THU',
          'days.short.friday': 'FRI',
          'days.short.saturday': 'SAT',
          'days.short.sunday': 'SUN',

          // Months
          'months.short.january': 'JAN',
          'months.short.february': 'FEB',
          'months.short.march': 'MAR',
          'months.short.april': 'APR',
          'months.short.may': 'MAY',
          'months.short.june': 'JUN',
          'months.short.july': 'JUL',
          'months.short.august': 'AUG',
          'months.short.september': 'SEP',
          'months.short.october': 'OCT',
          'months.short.november': 'NOV',
          'months.short.december': 'DEC',

          // Calendar
          'calendar.week': 'Week',
          'calendar.month': 'Month',
          'calendar.day': 'Day',
          'calendar.agenda': 'Agenda',
          'calendar.today': 'Today',
          'calendar.previous': 'Previous',
          'calendar.next': 'Next',
          'calendar.previousWeek': 'Previous Week',
          'calendar.nextWeek': 'Next Week',
          'calendar.previousMonth': 'Previous Month',
          'calendar.nextMonth': 'Next Month',
          'calendar.weekRange': 'Week of',
          'calendar.selectSlots': 'Click and drag to select time slots',
          'calendar.selectedSlots': 'Selected Time Slots',
          'calendar.clearSelection': 'Clear Selection',
          'calendar.dragToSelect': 'Drag to select time slots',
          'calendar.clickToAdd': 'Click to add time slot',
          'calendar.removeSlot': 'Remove slot',
          'calendar.helpTextDesktop': 'Click to create slots',
          'calendar.helpTextMobile': 'Tap to create slots',
          'calendar.helpTextTablet': 'Tap and hold to create slots',
          'calendar.allDay': 'All day',
          'calendar.customDuration': 'Custom',
          'calendar.viewMode': 'View Mode',
          'calendar.timeRange': 'Time Range',
          'calendar.resizeSlots': 'Drag the edges of slots to resize them',
          'calendar.moveSlots': 'Drag the entire slot to move it',
          'calendar.addTimeSlots': 'Add Time Slots',
          'calendar.clickTimeSlot': 'Click a time slot to add it to the selected date',
          'calendar.helpText': 'Drag the edges of slots to resize them or drag the entire slot to move it',
          'calendar.selectedDates': 'Selected Dates',
          'calendar.startTime': 'Start Time',
          'calendar.duration': 'Duration',
          'calendar.addHours': 'Add Hours',
          'calendar.clickDateToSelect': 'Click on a date to select time slots',
          'calendar.timeSlot': 'Time Slot',
          'calendar.mobileHint': 'Tap to create slots',

          // Dashboard
          'dashboard.totalPolls': 'Total Polls',
          'dashboard.totalParticipants': 'Total Participants',
          'dashboard.activePolls': 'Active Polls',
          'dashboard.completedPolls': 'Completed Polls',
          'dashboard.viewAnalytics': 'View Analytics',
          'dashboard.pollStatus.active': 'Active',
          'dashboard.pollStatus.closed': 'Closed',
          'dashboard.loading': 'Loading polls...',
          'dashboard.loadError': 'Failed to load polls',
          'dashboard.welcomeBack': 'Welcome back, {{name}}!',
          'dashboard.overviewDescription': 'Here\'s an overview of your meeting polls and scheduling activity.',
          'dashboard.recentPolls': 'Recent Polls',
          'dashboard.viewAll': 'View All',
          'dashboard.createdPolls': 'Created Polls',
          'dashboard.noPollsYet': 'No polls yet',
          'dashboard.createFirstPoll': 'Create your first poll to start scheduling meetings with your team.',
          'dashboard.noDescription': 'No description',
          'dashboard.unknownDate': 'Unknown date',
          'dashboard.timeSlots': 'time slots',
          'dashboard.participants': 'participants',
          'dashboard.editPoll': 'Edit Poll',
          'dashboard.viewPoll': 'View Poll',
          'dashboard.recentActivity': 'Recent Activity',
          'dashboard.noRecentActivity': 'No recent activity',
          'dashboard.recentActivityDescription': 'Your recent poll activity will appear here.',
          'dashboard.anonymousActivityDescription': 'Activity tracking is available for registered users. Create an account to see your poll activity.',
          'dashboard.activityLoading': 'Loading activity...',
          'dashboard.activity.pollCreated': 'created poll',
          'dashboard.activity.pollUpdated': 'updated poll',
          'dashboard.activity.userVoted': 'voted on',
          'dashboard.activity.profileUpdated': 'updated profile',
          'dashboard.activity.pollRemoved': 'removed poll',
          'dashboard.activity.pollClosed': 'closed poll',
          'dashboard.activity.pollReopened': 'reopened poll',
          'dashboard.activity.generalActivity': 'General Activity',
          'dashboard.activity.justNow': 'just now',
          'dashboard.activity.minutesAgo': '{{count}} minutes ago',
          'dashboard.activity.hoursAgo': '{{count}} hours ago',
          'dashboard.activity.daysAgo': '{{count}} days ago',
          'dashboard.activity.weeksAgo': '{{count}} weeks ago',
          'common.retry': 'Retry',

          // Polls Management Page
          'polls.title': 'My Polls',
          'polls.description': 'Manage all your meeting polls in one place.',
          'polls.searchPlaceholder': 'Search polls...',
          'polls.filterAll': 'All Polls',
          'polls.filterActive': 'Active',
          'polls.filterClosed': 'Closed',
          'polls.table.name': 'Poll Name',
          'polls.table.status': 'Status',
          'polls.table.participants': 'Participants',
          'polls.table.selectedSlot': 'Selected Slot',
          'polls.table.created': 'Created',
          'polls.table.actions': 'Actions',
          'polls.noPolls': 'No polls found',
          'polls.noPollsDesc': 'Create your first poll to get started.',
          'polls.deleteConfirm': 'Delete Poll',
          'polls.deleteConfirmDesc': 'Are you sure you want to delete this poll? This action cannot be undone.',
          'polls.deleteSuccess': 'Poll deleted successfully',
          'polls.deleteError': 'Failed to delete poll',
          'polls.sortBy': 'Sort by',
          'polls.itemsPerPage': 'Items per page',
          'polls.showing': 'Showing {{start}} to {{end}} of {{total}} polls',

          // Features page
          'features.subtitle': 'Discover all the powerful features that make Meet the best choice for scheduling meetings and coordinating with your team.',
          'features.smartTimezone.details': 'Automatically detects and converts time zones for all participants, ensuring everyone sees times in their local timezone without confusion.',
          'features.anonymous.details': 'Participants can vote on polls without creating an account, making it easier to get responses from external stakeholders and clients.',
          'features.realtime.details': 'See votes and responses update in real-time as participants respond, powered by Firebase real-time database technology.',
          'features.calendar.details': 'Once a time is decided, export the meeting directly to Google Calendar, Outlook, or download as an ICS file.',
          'features.responsive.details': 'Fully responsive design ensures a perfect experience on desktop, tablet, and mobile devices with touch-friendly interactions.',
          'features.smartScheduling.title': 'Smart Scheduling',
          'features.smartScheduling.description': 'Intelligent time slot suggestions based on working hours and preferences',
          'features.smartScheduling.details': 'Our system learns from usage patterns to suggest the most likely successful meeting times for your team.',
          'features.privacy.title': 'Privacy First',
          'features.privacy.description': 'Your data is secure and private with enterprise-grade security',
          'features.privacy.details': 'All data is encrypted in transit and at rest, with strict privacy controls and GDPR compliance.',
          'features.analytics.title': 'Analytics & Insights',
          'features.analytics.description': 'Get insights into your team\'s scheduling patterns and preferences',
          'features.analytics.details': 'Understand when your team is most available and optimize future meeting scheduling.',

          'features.cta.title': 'Ready to experience the future of meeting scheduling?',
          'features.cta.subtitle': 'Join thousands of teams who have streamlined their scheduling with Meet\'s intelligent features.',
          'features.cta.startCreating': 'Start Creating Polls',
          'features.cta.signUpFree': 'Sign Up Free',

          // Analytics
          'analytics.title': 'Analytics Dashboard',
          'analytics.subtitle': 'Insights and trends for your polls',
          'analytics.refresh': 'Refresh Data',
          'analytics.errorLoading': 'Failed to load analytics data',
          'analytics.noData': 'No analytics data available yet. Create some polls to see insights!',

          // Analytics KPIs
          'analytics.kpis.totalPolls': 'Total Polls',
          'analytics.kpis.totalParticipants': 'Total Participants',
          'analytics.kpis.avgParticipation': 'Avg. Participation',
          'analytics.kpis.avgResponseTime': 'Avg. Response Time',
          'analytics.kpis.activePolls': 'Active Polls',
          'analytics.kpis.completedPolls': 'Completed Polls',

          // Analytics Charts
          'analytics.charts.pollsOverTime': 'Polls & Participation Over Time',
          'analytics.charts.pollsOverTimeDesc': 'Track your poll creation and participation trends',
          'analytics.charts.polls': 'Polls Created',
          'analytics.charts.participants': 'Participants',
          'analytics.charts.totalPollsCreated': 'Total Polls Created',
          'analytics.charts.totalParticipations': 'Total Participations',

          'analytics.charts.participationRates': 'Poll Participation Rates',
          'analytics.charts.participationRatesDesc': 'See which polls get the most engagement',
          'analytics.charts.participationRate': 'Participation Rate',
          'analytics.charts.highParticipation': 'High',
          'analytics.charts.mediumParticipation': 'Medium',
          'analytics.charts.lowParticipation': 'Low',
          'analytics.charts.veryLowParticipation': 'Very Low',
          'analytics.charts.averageParticipation': 'Average Participation',

          'analytics.charts.activityTimeline': 'Activity Timeline',
          'analytics.charts.activityTimelineDesc': 'Monitor your polling activity over time',
          'analytics.charts.totalActivities': 'Total Activities',
          'analytics.charts.pollsCreated': 'Polls Created',
          'analytics.charts.votesReceived': 'Votes Received',
          'analytics.charts.pollsUpdated': 'Polls Updated',

          'analytics.charts.timeInsights': 'Time Preferences & Insights',
          'analytics.charts.timeInsightsDesc': 'Understand when people prefer to meet',
          'analytics.charts.peakHours': 'Peak Hours',
          'analytics.charts.voteDistribution': 'Vote Distribution',
          'analytics.charts.votes': 'votes',
          'analytics.charts.percentage': 'Percentage',
          'analytics.charts.mostActiveHour': 'Most Active Hour',
          'analytics.charts.mostCommonResponse': 'Most Common Response',

          // Analytics Insights
          'analytics.insights.title': 'Key Insights',
          'analytics.insights.popularTime': 'Most Popular Time',
          'analytics.insights.peakHour': 'Peak Activity Hour',
          'analytics.insights.avgDuration': 'Avg. Poll Duration',
          'analytics.insights.topUser': 'Top Participant',
          'analytics.insights.days': 'days',
          'analytics.insights.noData': 'No data',

          // Footer
          'footer.description': 'The smart way to schedule meetings with real-time collaboration, and seamless calendar integration.',
          'footer.copyright': '© {{year}} Meet. All rights reserved.',
          'footer.madeWith': 'Made with',
          'footer.forBetterMeetings': 'for better meetings',
          'footer.product.title': 'Product',
          'footer.product.pricing': 'Pricing',
          'footer.product.api': 'API',
          'footer.product.integrations': 'Integrations',
          'footer.company.title': 'Company',
          'footer.company.about': 'About',
          'footer.company.blog': 'Blog',
          'footer.company.careers': 'Careers',
          'footer.company.contact': 'Contact',
          'footer.support.title': 'Support',
          'footer.support.helpCenter': 'Help Center',
          'footer.support.documentation': 'Documentation',
          'footer.support.status': 'Status',
          'footer.support.community': 'Community',
          'footer.legal.title': 'Legal',
          'footer.legal.privacy': 'Privacy Policy',
          'footer.legal.terms': 'Terms of Service',
          'footer.legal.cookies': 'Cookie Policy',
          'footer.legal.gdpr': 'GDPR',

          // Help Center
          'help.title': 'Help Center',
          'help.subtitle': 'Hi, how can we help you?',
          'help.searchPlaceholder': 'Search for anything...',
          'help.popularTopics': 'Popular topics',
          'help.searchResults': 'Search Results',
          'help.articles': 'Articles',
          'help.learnMore': 'Learn more',
          'help.noResults': 'No results found',
          'help.noResultsDescription': 'Try adjusting your search terms or browse our popular topics below.',
          'help.tableOfContents': 'On this page',
          'help.lastUpdated': 'Last updated: {{date}}',
          'help.relatedArticles': 'Related Articles',
          'help.categories.all': 'All',
          'help.categories.gettingStarted.title': 'Getting Started',
          'help.categories.gettingStarted.description': 'Learn the basics of creating and managing polls',
          'help.categories.voting.title': 'Voting & Participants',
          'help.categories.voting.description': 'Understand how voting works and manage participants',
          'help.categories.features.title': 'Features & Settings',
          'help.categories.features.description': 'Explore advanced features and customization options',

          // Help Voting
          'help.voting.question': 'Was this article helpful?',
          'help.voting.yes': 'Yes',
          'help.voting.no': 'No',
          'help.voting.thankYou': 'Thank you for your feedback!',
          'help.voting.foundHelpful': 'We\'re glad this article was helpful.',
          'help.voting.foundNotHelpful': 'We\'ll work on improving this content.',
          'help.voting.feedback': 'Your feedback helps us improve our documentation.',

          // FAQ
          'help.faq.title': 'Frequently asked questions',
          'help.faq.createFirstPoll.question': 'How do I create my first poll?',
          'help.faq.createFirstPoll.answer': 'Click on "Create Poll" in the navigation bar, fill in your poll details, select time slots using our calendar interface, and share the link with participants.',
          'help.faq.participantsJoin.question': 'How do participants join my poll?',
          'help.faq.participantsJoin.answer': 'Share the poll link with participants. They can vote anonymously without creating an account, or sign in for a personalized experience.',
          'help.faq.reuseQuestions.question': 'Can I reuse questions across different polls?',
          'help.faq.reuseQuestions.answer': 'Currently, each poll is independent. However, you can copy time slots and settings from previous polls when creating new ones.',
          'help.faq.viewResults.question': 'How do I view the results of a poll?',
          'help.faq.viewResults.answer': 'Results are displayed in real-time on the poll page. You can see vote counts, participant names, and export the final schedule to your calendar.',
          'help.faq.customizeAppearance.question': 'Can I customize the appearance of my polls?',
          'help.faq.customizeAppearance.answer': 'Yes! You can choose between light and dark themes, and the interface automatically adapts to your preferences.',

          // Help Articles
          'help.articles.creatingFirstPoll.title': 'Creating Your First Poll',
          'help.articles.creatingFirstPoll.welcome.title': 'Welcome to Meet',
          'help.articles.creatingFirstPoll.welcome.content': 'Learn the basics of setting up your account and creating your first interactive learning poll on our platform.',
          'help.articles.creatingFirstPoll.accountSetup.title': 'Setting Up Your Account',
          'help.articles.creatingFirstPoll.accountSetup.content': 'Before you can start creating polls, you\'ll need to set up your account with your basic information and preferences.',
          'help.articles.creatingFirstPoll.accountSetup.signup.title': 'Sign Up Process',
          'help.articles.creatingFirstPoll.accountSetup.signup.content': 'Complete your profile by adding your name and profile picture in the Profile section.\nSet your preferred language in the Settings page.\nAdd your organization or team information details in the Settings page.\nConfigure your poll bank settings to determine how questions are saved and reused.',
          'help.articles.creatingFirstPoll.accountSetup.anonymous.title': 'Anonymous Option',
          'help.articles.creatingFirstPoll.accountSetup.anonymous.content': 'You can also create polls without signing up by using our anonymous mode. Simply click "Create Poll" and start building your poll immediately.',
          'help.articles.creatingFirstPoll.creating.title': 'Creating Your First Poll',
          'help.articles.creatingFirstPoll.creating.content': 'Creating a poll is simple and straightforward. Follow these steps to create your first interactive learning poll.',
          'help.articles.creatingFirstPoll.creating.basicInfo.title': 'Basic Information',
          'help.articles.creatingFirstPoll.creating.basicInfo.content': 'Enter the Dashboard, click "New Poll", and select the type of poll you want to create: Book, Team, or Offline.\nAdd questions to your poll: You can create multiple-choice, true/false, and other question types, or generate questions using AI.\nAdd questions using AI.',
          'help.articles.creatingFirstPoll.creating.timeSlots.title': 'Time Slots',
          'help.articles.creatingFirstPoll.creating.timeSlots.content': 'Configure additional settings like time limits, password protection, and automatic question saving.\nClick "Create Poll" to finalize and save your poll.',
          'help.articles.creatingFirstPoll.sharing.title': 'Sharing Your Poll',
          'help.articles.creatingFirstPoll.sharing.content': 'After creating your poll, you\'ll need to invite participants to join. Share this code with your participants through email, messaging apps, or by displaying it on a screen.\nFor added security, you can enable password protection for your poll.\nYou can also download reports for offline analysis.',

          'help.articles.pollSettings.title': 'Poll Settings Guide',
          'help.articles.pollSettings.overview.title': 'Settings Overview',
          'help.articles.pollSettings.overview.content': 'Poll settings allow you to customize how your poll behaves and appears to participants.',
          'help.articles.pollSettings.basic.title': 'Basic Settings',
          'help.articles.pollSettings.basic.content': 'Configure basic poll options like title, description, and time slots.',
          'help.articles.pollSettings.advanced.title': 'Advanced Settings',
          'help.articles.pollSettings.advanced.content': 'Set up advanced features like multiple votes, deadlines, and participant restrictions.',

          'help.articles.voting.title': 'Voting Guide',
          'help.articles.voting.howTo.title': 'How to Vote',
          'help.articles.voting.howTo.content': 'Participants can vote by clicking on their preferred time slots. The interface is intuitive and mobile-friendly.',
          'help.articles.voting.options.title': 'Vote Options',
          'help.articles.voting.options.content': 'Choose from Yes (green), Maybe (orange), or No (red) for each time slot.',
          'help.articles.voting.anonymous.title': 'Anonymous Voting',
          'help.articles.voting.anonymous.content': 'Participants can vote without creating an account, making it easy for external stakeholders to participate.',

          'help.articles.participants.title': 'Managing Participants',
          'help.articles.participants.inviting.title': 'Inviting Participants',
          'help.articles.participants.inviting.content': 'Share the poll link with participants via email, messaging, or social media.',
          'help.articles.participants.management.title': 'Participant Management',
          'help.articles.participants.management.content': 'View participant responses in real-time and manage their access to the poll.',

          'help.articles.calendar.title': 'Calendar Export',
          'help.articles.calendar.export.title': 'Export Options',
          'help.articles.calendar.export.content': 'Export your finalized meeting times directly to your calendar application.',
          'help.articles.calendar.formats.title': 'Supported Formats',
          'help.articles.calendar.formats.content': 'We support Google Calendar, Outlook, and ICS file formats for maximum compatibility.',

          'help.articles.timezone.title': 'Timezone Support',
          'help.articles.timezone.howItWorks.title': 'How It Works',
          'help.articles.timezone.howItWorks.content': 'Our system automatically detects and converts timezones for all participants.',
          'help.articles.timezone.setting.title': 'Setting Your Timezone',
          'help.articles.timezone.setting.content': 'Your timezone is automatically detected, but you can manually adjust it in your profile settings.',

          // New Help Articles
          'help.articles.emailEmbedding.title': 'Poll Email Embedding',
          'help.articles.emailEmbedding.overview.title': 'Overview',
          'help.articles.emailEmbedding.overview.content': 'Email embedding allows you to include your poll directly in email messages, making it easy for participants to vote without leaving their inbox. This feature supports HTML tables with inline CSS and direct voting links.',
          'help.articles.emailEmbedding.howTo.title': 'How to Embed Polls in Emails',
          'help.articles.emailEmbedding.howTo.content': 'To embed a poll in an email:\n\n1. Open your poll and click the "Share" button\n2. Select "Email Embedding" from the sharing options\n3. Choose your preferred embedding format (HTML table or simple links)\n4. Copy the generated HTML code\n5. Paste it into your email client that supports HTML\n6. Send the email to your participants\n\nParticipants can click directly on time slots to vote, and their votes will be recorded automatically.',
          'help.articles.emailEmbedding.htmlCode.title': 'HTML Code and Styling',
          'help.articles.emailEmbedding.htmlCode.content': 'The generated HTML code uses inline CSS to ensure compatibility across different email clients. The embedded poll includes:\n\n• A formatted table showing all available time slots\n• Direct voting links for each time slot\n• Responsive design that works on mobile devices\n• Consistent styling that matches your poll theme\n\nYou can customize the appearance by modifying the inline CSS styles before sending.',
          'help.articles.emailEmbedding.directVoting.title': 'Direct Voting Links',
          'help.articles.emailEmbedding.directVoting.content': 'Each time slot in the embedded poll includes a direct voting link that:\n\n• Takes participants directly to the voting interface\n• Pre-selects the time slot they clicked\n• Works with anonymous voting (no login required)\n• Tracks votes in real-time\n• Provides immediate feedback after voting\n\nThis seamless integration eliminates the need for participants to navigate through multiple pages to cast their votes.',

          'help.articles.analytics.title': 'Analytics Dashboard',
          'help.articles.analytics.overview.title': 'Overview',
          'help.articles.analytics.overview.content': 'The analytics dashboard provides comprehensive insights into your polls, helping you understand participation patterns, voting trends, and overall engagement. Access analytics by clicking "View Analytics" from your dashboard or individual poll pages.',
          'help.articles.analytics.kpis.title': 'Key Performance Indicators (KPIs)',
          'help.articles.analytics.kpis.content': 'The analytics dashboard displays several important KPIs:\n\n• Total number of polls created\n• Total participants across all polls\n• Active vs. completed polls ratio\n• Average response time\n• Participation rate percentage\n• Most popular time slots\n\nThese metrics help you understand how effectively you\'re coordinating meetings and identify areas for improvement.',
          'help.articles.analytics.charts.title': 'Charts and Visualizations',
          'help.articles.analytics.charts.content': 'Visual representations of your data include:\n\n• Participation trends over time\n• Vote distribution across time slots\n• Response patterns by day of week\n• Poll completion rates\n• Geographic distribution of participants (when available)\n\nAll charts are interactive and can be filtered by date range, poll type, or other criteria.',
          'help.articles.analytics.insights.title': 'Actionable Insights',
          'help.articles.analytics.insights.content': 'The analytics system provides actionable insights such as:\n\n• Optimal meeting times based on historical data\n• Participant engagement recommendations\n• Best practices for poll creation\n• Suggestions for improving response rates\n\nUse these insights to optimize your meeting scheduling process and increase participant satisfaction.',

          'help.articles.accountMatching.title': 'Anonymous and Account Matching',
          'help.articles.accountMatching.overview.title': 'Overview',
          'help.articles.accountMatching.overview.content': 'Our platform allows anonymous users to create polls and vote without creating an account. Later, if they decide to create an account (Gmail or email), their existing poll data and votes can be preserved and migrated to their new account.',
          'help.articles.accountMatching.howItWorks.title': 'How Account Matching Works',
          'help.articles.accountMatching.howItWorks.content': 'When an anonymous user creates an account:\n\n1. The system identifies polls and votes associated with their browser session\n2. During account creation, users are prompted to claim their anonymous data\n3. All polls, votes, and preferences are transferred to the new account\n4. The user maintains access to all their previous activity\n5. Future activity is automatically linked to their account\n\nThis process is seamless and ensures no data is lost during the transition.',
          'help.articles.accountMatching.dataPreservation.title': 'Data Preservation',
          'help.articles.accountMatching.dataPreservation.content': 'The following data is preserved during account migration:\n\n• All polls created as an anonymous user\n• Voting history and preferences\n• Custom avatars and display names\n• Poll settings and configurations\n• Participant interactions and comments\n\nYour anonymous activity becomes part of your permanent account history, allowing for continuous analytics and insights.',
          'help.articles.accountMatching.migration.title': 'Migration Process',
          'help.articles.accountMatching.migration.content': 'The migration process is automatic and secure:\n\n1. Create your account using Gmail or email signup\n2. Confirm your email address if using email signup\n3. The system will detect and offer to migrate your anonymous data\n4. Accept the migration to transfer all your data\n5. Your account is now fully set up with your historical data\n\nIf you encounter any issues during migration, contact our support team for assistance.',

          // Poll Status Management Help Article
          'help.articles.pollStatus.title': 'Poll Status Management',
          'help.articles.pollStatus.overview.title': 'Overview',
          'help.articles.pollStatus.overview.content': 'Understanding how to manage poll statuses is essential for effective meeting coordination. This guide covers the different poll statuses, how to close and reopen polls, and the various automatic closure conditions available.',

          'help.articles.pollStatus.statuses.title': 'Poll Statuses',
          'help.articles.pollStatus.statuses.content': 'Polls can have two main statuses: Active and Closed. Each status determines what actions participants can take and how the poll behaves.',

          'help.articles.pollStatus.statuses.active.title': 'Active Status',
          'help.articles.pollStatus.statuses.active.content': 'When a poll is active, participants can vote, change their votes, and new participants can join. The poll creator can edit poll details, add or remove time slots, and manage settings. Active polls display real-time updates as votes are cast.',

          'help.articles.pollStatus.statuses.closed.title': 'Closed Status',
          'help.articles.pollStatus.statuses.closed.content': 'When a poll is closed, no new votes can be cast and existing votes cannot be modified. The results are finalized and participants can only view the final outcome. Closed polls display the winning time slot and closure reason.',

          'help.articles.pollStatus.closing.title': 'Closing Polls',
          'help.articles.pollStatus.closing.content': 'Polls can be closed manually by the creator or automatically based on predefined conditions. When closing a poll, you can optionally select a winning time slot.',

          'help.articles.pollStatus.closing.manual.title': 'Manual Closing',
          'help.articles.pollStatus.closing.manual.content': 'To manually close a poll, navigate to your poll and click the "Close Poll" button. You can choose to automatically select the most popular time slot or manually select a specific slot as the winner. Once closed, participants will see the final results.',

          'help.articles.pollStatus.closing.automatic.title': 'Automatic Closing',
          'help.articles.pollStatus.closing.automatic.content': 'Polls can be configured to close automatically based on three conditions: deadline reached, participant limit reached, or majority threshold achieved. These settings can be configured when creating or editing a poll in the "Auto-close Settings" section.',

          'help.articles.pollStatus.reopening.title': 'Reopening Polls',
          'help.articles.pollStatus.reopening.content': 'Poll creators can reopen closed polls at any time by clicking the "Reopen Poll" button. When reopening a poll that was automatically closed, the system will remove or adjust the condition that caused the closure to prevent immediate re-closure. For example, if a poll was closed due to a deadline, the deadline will be removed when reopening.',

          'help.articles.pollStatus.reasons.title': 'Closure Reasons',
          'help.articles.pollStatus.reasons.content': 'When a poll is closed, the system records the reason for closure. This helps participants understand why voting ended and provides transparency in the decision-making process.',

          'help.articles.pollStatus.reasons.manual.title': 'Manual Closure',
          'help.articles.pollStatus.reasons.manual.content': 'The poll creator manually decided to close the poll. This typically happens when the creator has gathered enough responses or wants to finalize the meeting time.',

          'help.articles.pollStatus.reasons.deadline.title': 'Deadline Reached',
          'help.articles.pollStatus.reasons.deadline.content': 'The poll was automatically closed because the voting deadline was reached. This ensures that meeting planning stays on schedule and prevents indefinite voting periods.',

          'help.articles.pollStatus.reasons.participantLimit.title': 'Participant Limit Reached',
          'help.articles.pollStatus.reasons.participantLimit.content': 'The poll was automatically closed because the maximum number of participants was reached. This is useful for meetings with limited capacity or when you want to close voting after a specific number of responses.',

          'help.articles.pollStatus.reasons.majority.title': 'Majority Threshold Achieved',
          'help.articles.pollStatus.reasons.majority.content': 'The poll was automatically closed because a majority of expected participants voted for the same time slot. This allows for quick decision-making when there is clear consensus among participants.',

          // Profile page
          'profile.title': 'Profile Settings',
          'profile.subtitle': 'Manage your account settings and preferences',
          'profile.personalInfo': 'Personal Information',
          'profile.personalInfoDesc': 'Update your display name and avatar',
          'profile.displayName': 'Display Name',
          'profile.displayNamePlaceholder': 'Enter your display name',
          'profile.googleDisplayNameNote': 'You can customize your display name here. Your Google account name will remain unchanged.',
          'profile.avatar': 'Avatar',
          'profile.avatarDesc': 'Choose an avatar to represent you',
          'profile.googleAvatarDesc': 'Using your Google profile picture. Click refresh to choose a custom avatar.',
          'profile.refreshAvatar': 'Refresh Avatar',
          'profile.uploadOwnImage': 'Upload Own Image',
          'profile.avatarUpdated': 'Avatar updated successfully',
          'profile.randomAvatar': 'Random Avatar',
          'profile.useGoogleAvatar': 'Use Google Avatar',
          'profile.authMethod': 'Authentication Method',
          'profile.authMethodDesc': 'How you sign in to your account',
          'profile.accountSecurity': 'Account Security',
          'profile.accountSecurityDesc': 'Change your password and security settings',
          'profile.currentPassword': 'Current Password',
          'profile.newPassword': 'New Password',
          'profile.confirmNewPassword': 'Confirm New Password',
          'profile.passwordRequirements': 'Password must be at least 6 characters long',
          'profile.passwordStrength': 'Password Strength',
          'profile.passwordStrengthWeak': 'Weak',
          'profile.passwordStrengthMedium': 'Medium',
          'profile.passwordStrengthStrong': 'Strong',
          'profile.changePassword': 'Change Password',
          'profile.googleManaged': 'Google Account',
          'profile.googleManagedDesc': 'Your email and password are managed by Google',
          'profile.anonymousAccount': 'Anonymous Account',
          'profile.anonymousAccountDesc': 'You are using an anonymous account',
          'profile.upgradeAccount': 'Upgrade Account',
          'profile.linkToGoogle': 'Link to Google',
          'profile.createEmailAccount': 'Create Email Account',
          'profile.saveChanges': 'Save Changes',
          'profile.unsavedChanges': 'Unsaved Changes',
          'profile.unsavedChangesDesc': 'You have unsaved changes. Are you sure you want to leave?',
          'profile.discardChanges': 'Discard Changes',
          'profile.profileUpdated': 'Profile updated successfully',
          'profile.passwordChanged': 'Password changed successfully',
          'profile.displayNameRequired': 'Display name is required',
          'profile.displayNameTooShort': 'Display name must be at least 2 characters',
          'profile.currentPasswordRequired': 'Current password is required',
          'profile.newPasswordRequired': 'New password is required',
          'profile.confirmPasswordRequired': 'Please confirm your new password',
          'profile.passwordsDoNotMatch': 'Passwords do not match',
          'profile.currentPasswordIncorrect': 'Current password is incorrect',
          'profile.emailPlaceholder': 'Enter your email',
          'profile.currentPasswordPlaceholder': 'Enter your current password',
          'profile.newPasswordPlaceholder': 'Enter your new password',
          'profile.confirmNewPasswordPlaceholder': 'Confirm your new password',
          'profile.emailAccount': 'Email Account',
          'profile.dataMigrationNote': 'Your polls and data will be transferred to your new account.',
        },
      },
      es: {
        common: {
          // Navigation
          'nav.home': 'Inicio',
          'nav.features': 'Características',
          'nav.howItWorks': 'Cómo Funciona',
          'nav.help': 'Ayuda',
          'nav.createPoll': 'Crear Encuesta',
          'nav.login': 'Iniciar Sesión',
          'nav.signup': 'Registrarse',
          'nav.logout': 'Cerrar Sesión',
          'nav.profile': 'Perfil',
          'nav.dashboard': 'Panel',
          'nav.polls': 'Encuestas',
          'nav.analytics': 'Analíticas',
          'nav.settings': 'Configuración',
          'nav.language': 'Idioma',
          'nav.theme': 'Tema',
          'nav.light': 'Claro',
          'nav.dark': 'Oscuro',

          // Common actions
          'common.save': 'Guardar',
          'common.cancel': 'Cancelar',
          'common.delete': 'Eliminar',
          'common.edit': 'Editar',
          'common.create': 'Crear',
          'common.submit': 'Enviar',
          'common.loading': 'Cargando...',
          'common.error': 'Error',
          'common.success': 'Éxito',
          'common.close': 'Cerrar',
          'common.back': 'Atrás',
          'common.next': 'Siguiente',
          'common.previous': 'Anterior',
          'common.continue': 'Continuar',
          'common.finish': 'Finalizar',
          'common.dismiss': 'Descartar',
          'common.saving': 'Guardando...',
          'common.user': 'Usuario',

          // Authentication
          'auth.login': 'Iniciar Sesión',
          'auth.signup': 'Registrarse',
          'auth.logout': 'Cerrar Sesión',
          'auth.email': 'Correo Electrónico',
          'auth.password': 'Contraseña',
          'auth.confirmPassword': 'Confirmar Contraseña',
          'auth.forgotPassword': '¿Olvidaste tu contraseña?',
          'auth.resetPassword': 'Restablecer Contraseña',
          'auth.signInWithGoogle': 'Iniciar sesión con Google',
          'auth.createAccount': 'Crear Cuenta',
          'auth.alreadyHaveAccount': '¿Ya tienes una cuenta?',
          'auth.dontHaveAccount': '¿No tienes una cuenta?',
          'auth.loginSuccess': '¡Sesión iniciada exitosamente!',
          'auth.logoutSuccess': '¡Sesión cerrada exitosamente!',
          'auth.signupSuccess': '¡Cuenta creada exitosamente!',
          'auth.invalidCredentials': 'Email o contraseña inválidos',
          'auth.emailRequired': 'El email es requerido',
          'auth.passwordRequired': 'La contraseña es requerida',
          'auth.passwordTooShort': 'La contraseña debe tener al menos 6 caracteres',
          'auth.passwordsDoNotMatch': 'Las contraseñas no coinciden',
          'auth.emailInvalid': 'Por favor ingresa un email válido',
          'auth.fullName': 'Nombre Completo',
          'auth.fullNameRequired': 'El nombre completo es requerido',
          'auth.fullNameTooShort': 'El nombre debe tener al menos 2 caracteres',
          'auth.confirmPasswordRequired': 'Por favor confirma tu contraseña',
          'auth.welcomeBack': 'Bienvenido de vuelta a Meet',
          'auth.joinMeet': 'Únete a Meet y comienza a programar de manera más inteligente',
          'auth.orContinueWith': 'O continúa con',
          'auth.emailAlreadyInUse': 'El email ya está en uso',
          'auth.createEmailAccount': 'Crear Cuenta de Email',
          'auth.signInWithGoogleAccount': 'Iniciar sesión con Google',
          'auth.accountCreationOptions': 'Crea una cuenta para guardar tus datos permanentemente',
          'auth.passwordStrength': 'Fortaleza de Contraseña',
          'auth.passwordWeak': 'Débil',
          'auth.passwordMedium': 'Medio',
          'auth.passwordStrong': 'Fuerte',
          'auth.dataMigrationSuccess': '¡Tus datos han sido transferidos exitosamente a tu nueva cuenta!',
          'auth.dataMigrationError': 'Hubo un error al transferir tus datos. Por favor intenta de nuevo.',
          'auth.passwordPlaceholder': 'Ingresa tu contraseña',
          'auth.confirmPasswordPlaceholder': 'Confirma tu contraseña',

          // Settings
          'settings.title': 'Configuración',
          'settings.subtitle': 'Gestiona tus preferencias y configuración de cuenta',
          'settings.timezone': 'Zona Horaria',
          'settings.timezoneDesc': 'Anular la zona horaria de tu navegador para encuestas',
          'settings.calendarStartDay': 'Día de Inicio del Calendario',
          'settings.calendarStartDayDesc': 'Elige qué día comienza tu semana del calendario',
          'settings.monday': 'Lunes',
          'settings.sunday': 'Domingo',
          'settings.useSystemTimezone': 'Usar zona horaria del sistema',
          'settings.saveSettings': 'Guardar Configuración',
          'settings.settingsUpdated': '¡Configuración actualizada exitosamente!',
          'settings.settingsError': 'Error al actualizar la configuración',

          // Error messages
          'error.generic': 'Algo salió mal. Por favor intenta de nuevo.',
          'error.network': 'Error de red. Por favor verifica tu conexión.',
          'error.unauthorized': 'No estás autorizado para realizar esta acción.',
          'error.notFound': 'El recurso solicitado no fue encontrado.',
          'error.serverError': 'Error del servidor. Por favor intenta más tarde.',
          'error.tryAgain': 'Intentar de Nuevo',

          // Success messages
          'success.saved': '¡Guardado exitosamente!',
          'success.deleted': '¡Eliminado exitosamente!',
          'success.updated': '¡Actualizado exitosamente!',
          'success.created': '¡Creado exitosamente!',
          'success.sent': '¡Enviado exitosamente!',

          // Time and dates
          'time.today': 'Hoy',
          'time.tomorrow': 'Mañana',
          'time.yesterday': 'Ayer',
          'time.thisWeek': 'Esta Semana',
          'time.nextWeek': 'Próxima Semana',
          'time.thisMonth': 'Este Mes',
          'time.nextMonth': 'Próximo Mes',
          'time.selectDate': 'Seleccionar Fecha',
          'time.selectTime': 'Seleccionar Hora',
          'time.duration': 'Duración',
          'time.timezone': 'Zona Horaria',
          'time.slotDuration': 'Duración de Franja Horaria',
          'time.30minutes': '30 minutos',
          'time.1hour': '1 hora',
          'time.2hours': '2 horas',
          'time.custom': 'Personalizado',
          'time.customDuration': 'Duración Personalizada',
          'time.minutes': 'minutos',
          'time.hours': 'horas',
          'time.unit': 'Unidad',
          'time.selectDuration': 'Selecciona la duración para las franjas horarias',
          'time.timezoneInfo': 'Las horas se muestran en tu zona horaria local',

          // Days
          'days.short.monday': 'LUN',
          'days.short.tuesday': 'MAR',
          'days.short.wednesday': 'MIÉ',
          'days.short.thursday': 'JUE',
          'days.short.friday': 'VIE',
          'days.short.saturday': 'SÁB',
          'days.short.sunday': 'DOM',

          // Months
          'months.short.january': 'ENE',
          'months.short.february': 'FEB',
          'months.short.march': 'MAR',
          'months.short.april': 'ABR',
          'months.short.may': 'MAY',
          'months.short.june': 'JUN',
          'months.short.july': 'JUL',
          'months.short.august': 'AGO',
          'months.short.september': 'SEP',
          'months.short.october': 'OCT',
          'months.short.november': 'NOV',
          'months.short.december': 'DIC',

          // Calendar
          'calendar.week': 'Semana',
          'calendar.month': 'Mes',
          'calendar.day': 'Día',
          'calendar.agenda': 'Agenda',
          'calendar.today': 'Hoy',
          'calendar.previous': 'Anterior',
          'calendar.next': 'Siguiente',
          'calendar.previousWeek': 'Semana Anterior',
          'calendar.nextWeek': 'Semana Siguiente',
          'calendar.previousMonth': 'Mes Anterior',
          'calendar.nextMonth': 'Mes Siguiente',
          'calendar.weekRange': 'Semana del',
          'calendar.selectSlots': 'Haz clic y arrastra para seleccionar horarios',
          'calendar.selectedSlots': 'Horarios Seleccionados',
          'calendar.clearSelection': 'Limpiar Selección',
          'calendar.dragToSelect': 'Arrastra para seleccionar horarios',
          'calendar.clickToAdd': 'Haz clic para añadir horario',
          'calendar.removeSlot': 'Eliminar horario',
          'calendar.helpTextDesktop': 'Clic para crear horarios',
          'calendar.helpTextMobile': 'Toca para crear horarios',
          'calendar.helpTextTablet': 'Mantén presionado para crear horarios',
          'calendar.allDay': 'Todo el día',
          'calendar.customDuration': 'Personalizado',
          'calendar.viewMode': 'Modo de Vista',
          'calendar.timeRange': 'Rango de Tiempo',
          'calendar.resizeSlots': 'Arrastra los bordes de los horarios para redimensionarlos',
          'calendar.moveSlots': 'Arrastra todo el horario para moverlo',
          'calendar.addTimeSlots': 'Añadir Horarios',
          'calendar.clickTimeSlot': 'Haz clic en un horario para añadirlo a la fecha seleccionada',
          'calendar.helpText': 'Arrastra los bordes de los horarios para redimensionarlos o arrastra todo el horario para moverlo',
          'calendar.selectedDates': 'Fechas seleccionadas',
          'calendar.startTime': 'Hora de inicio',
          'calendar.duration': 'Duración',
          'calendar.addHours': 'Añadir horas',
          'calendar.clickDateToSelect': 'Haz clic en una fecha para seleccionar horarios',
          'calendar.timeSlot': 'Horario',
          'calendar.mobileHint': 'Toca para crear horarios',

          // Poll creation
          'poll.create': 'Crear Encuesta',
          'poll.title': 'Título de la Encuesta',
          'poll.description': 'Descripción',
          'poll.timeSlots': 'Horarios',
          'poll.addTimeSlot': 'Agregar Horario',
          'poll.removeTimeSlot': 'Eliminar Horario',
          'poll.settings': 'Configuración',
          'poll.allowMultipleVotes': 'Permitir múltiples votos por persona',
          'poll.deadline': 'Fecha límite de votación',
          'poll.deadlinePlaceholder': 'Seleccionar fecha límite de votación (opcional)',

          'poll.createSuccess': '¡Encuesta creada exitosamente!',
          'poll.createError': 'Error al crear la encuesta',
          'poll.titleRequired': 'El título de la encuesta es requerido',
          'poll.timeSlotsRequired': 'Se requiere al menos un horario',
          'poll.selectTimeSlots': 'Seleccionar horarios disponibles',
          'poll.view': 'Ver',
          'poll.viewPoll': 'Ver Encuesta',
          'poll.copyLink': 'Copiar Enlace',
          'poll.goToDashboard': 'Ir al Panel',
          'poll.createAnother': 'Crear Otra Encuesta',
          'poll.successMessage': '¡Tu encuesta ha sido creada y está lista para compartir!',
          'poll.reset': 'Reiniciar',
          'poll.titlePlaceholder': 'Ingresa el título de la encuesta',
          'poll.descriptionPlaceholder': 'Descripción opcional para tu encuesta',
          'poll.noTimeSlots': 'Aún no se han agregado horarios',
          'poll.noTimeSlotsDesc': 'Haz clic en "Agregar Horario" para comenzar',

          // Poll voting
          'vote.cast': 'Votar',
          'vote.update': 'Actualizar Voto',
          'vote.remove': 'Eliminar Voto',
          'vote.yourName': 'Tu Nombre',
          'vote.selectOptions': 'Selecciona tus horarios disponibles',
          'vote.voteSuccess': '¡Voto enviado exitosamente!',
          'vote.voteError': 'Error al enviar el voto',
          'vote.nameRequired': 'El nombre es requerido',
          'vote.optionsRequired': 'Por favor selecciona al menos una opción',
          'vote.participants': 'participantes',
          'vote.yourVote': 'Tu Voto',
          'vote.multipleAllowed': 'Puedes seleccionar múltiples horarios',
          'vote.singleOnly': 'Solo puedes seleccionar un horario',
          'vote.edit': 'Editar Voto',
          'vote.submitted': 'Voto Enviado',
          'vote.justSubmitted': '¡Voto Recién Enviado!',
          'vote.submittedAs': 'Votaste como',
          'vote.namePlaceholder': 'Ingresa tu nombre',
          'vote.updateSuccess': '¡Voto actualizado exitosamente!',
          'vote.deleteSuccess': '¡Voto eliminado exitosamente!',
          'vote.deleteError': 'Error al eliminar el voto',
          'vote.autoSavedFromEmail': '¡Tu voto para {{slot}} ha sido guardado automáticamente!',
          'vote.autoSaveError': 'Error al guardar automáticamente tu voto',
          'vote.vote': 'voto',
          'vote.votes': 'votos',
          'vote.matrixTitle': 'Vota en los Horarios Disponibles',
          'vote.you': 'Tú',
          'vote.anonymousUser': 'Tú (anónimo)',
          'vote.editName': 'Editar nombre',
          'vote.changeAvatar': 'Cambiar avatar',
          'vote.enterName': 'Ingresa tu nombre',
          'vote.anonymous': 'Anónimo',
          'vote.yes': 'Sí',
          'vote.maybe': 'Tal vez',
          'vote.no': 'No',

          // Poll results
          'results.title': 'Resultados de la Encuesta',
          'results.noVotes': 'Aún no hay votos',
          'results.noVotesDesc': '¡Sé el primero en votar en esta encuesta!',
          'results.totalVotes': 'Total de Votos',
          'results.mostPopular': 'Más Popular',
          'results.exportCalendar': 'Exportar al Calendario',
          'results.sharePoll': 'Compartir Encuesta',
          'results.closePoll': 'Cerrar Encuesta',
          'results.reopenPoll': 'Reabrir Encuesta',
          'results.liveUpdates': 'Los resultados se actualizan en tiempo real',
          'results.export': 'Exportar',
          'results.share': 'Compartir',
          'results.timeSlots': 'Horarios',
          'results.voteDistribution': 'Distribución de Votos',
          'results.votePercentages': 'Porcentajes de Votos',
          'results.detailedResults': 'Resultados Detallados',
          'results.timeSlot': 'Horario',
          'results.votes': 'Votos',
          'results.percentage': 'Porcentaje',
          'results.voters': 'Votantes',
          'results.noVoters': 'Sin votos',
          'results.winningTimeSlot': 'Horario Más Popular',

          // Poll management
          'poll.notFound': 'Encuesta No Encontrada',
          'poll.notFoundDesc': 'La encuesta que buscas no existe o ha sido eliminada.',
          'poll.loadError': 'Error al cargar la encuesta',
          'poll.createdBy': 'Creado por',
          'poll.closed': 'Votación Cerrada',
          'poll.votingClosed': 'La votación para esta encuesta ha sido cerrada',
          'poll.share': 'Compartir Encuesta',
          'poll.linkCopied': '¡Enlace de la encuesta copiado al portapapeles!',
          'poll.copyError': 'Error al copiar el enlace',
          'poll.availableTimeSlots': 'Horarios Disponibles',
          'poll.edit': 'Editar Encuesta',
          'poll.editSuccess': '¡Encuesta actualizada exitosamente!',
          'poll.editError': 'Error al actualizar la encuesta',
          'poll.editPoll': 'Editar Encuesta',
          'poll.updatePoll': 'Actualizar Encuesta',

          // Email embedding
          'poll.embedInEmail': 'Insertar en Email',
          'poll.emailEmbed': 'Insertar Email',
          'poll.emailEmbedTitle': 'Insertar Encuesta en Email',
          'poll.emailEmbedDescription': 'Genera HTML compatible con email que puedes copiar y pegar en tus correos. Los destinatarios pueden votar directamente haciendo clic en los horarios.',
          'poll.emailEmbedGenerate': 'Generar HTML de Email',
          'poll.emailEmbedCopy': 'Copiar HTML',
          'poll.emailEmbedCopied': '¡HTML de email copiado al portapapeles!',
          'poll.emailEmbedCopyError': 'Error al copiar HTML',
          'poll.emailEmbedCopyRendered': 'Copiar para Email',
          'poll.emailEmbedRenderedCopied': '¡Contenido de email copiado al portapapeles!',
          'poll.emailEmbedPreview': 'Vista Previa',
          'poll.emailEmbedInstructions': 'Usa el botón "Copiar para Email" arriba para mejores resultados, o copia el código HTML de abajo.',
          'poll.emailEmbedCompatibility': 'Compatible con Gmail, Outlook, Apple Mail y la mayoría de clientes de email.',
          'poll.emailEmbedHtmlStyle': 'Estilo HTML',
          'poll.emailEmbedRich': 'Rico (Recomendado)',
          'poll.emailEmbedSimple': 'Simple (Alta Compatibilidad)',
          'poll.emailEmbedHtmlSourceCode': 'Código Fuente HTML',
          'poll.emailEmbedInstructionsTitle': 'Instrucciones',
          'poll.emailEmbedOption1Title': 'Opción 1 (Recomendada):',
          'poll.emailEmbedOption1Description': 'Usa el botón "Copiar para Email" en la vista previa de arriba. Esto funciona mejor con Gmail y la mayoría de clientes de email modernos.',
          'poll.emailEmbedOption2Title': 'Opción 2:',
          'poll.emailEmbedOption2Description': 'Copia el código fuente HTML de abajo y pégalo en el editor HTML de tu email.',
          'poll.emailEmbedRecipientsNote': 'Los destinatarios pueden hacer clic en los horarios para votar directamente desde el email.',
          'poll.preSelectedSlot': 'Pre-seleccionado desde email',
          'poll.editDescription': 'Realiza cambios a tu encuesta',
          'poll.createSubtitle': 'Crea una nueva encuesta para encontrar el horario perfecto de reunión para tu equipo',
          'poll.authError': 'Error al autenticar usuario',
          'poll.allowMultipleVotesDesc': 'Permitir a los participantes seleccionar múltiples franjas horarias',

          // Poll closure settings
          'poll.autoCloseSettings': 'Configuración de Cierre Automático',
          'poll.autoCloseExplanation': 'La encuesta se cerrará automáticamente cuando se cumpla la primera condición.',
          'poll.enableDeadlineClose': 'Cerrar por Fecha Límite',
          'poll.enableDeadlineCloseDesc': 'Cerrar automáticamente la encuesta cuando se alcance la fecha límite',
          'poll.enableParticipantLimitClose': 'Cerrar por Límite de Participantes',
          'poll.enableParticipantLimitCloseDesc': 'Cerrar automáticamente la encuesta cuando se alcance el máximo de participantes',
          'poll.maxParticipants': 'Máximo de Participantes',
          'poll.maxParticipantsDesc': 'Cerrar automáticamente la encuesta cuando esta cantidad de personas hayan votado',
          'poll.maxParticipantsPlaceholder': 'ej. 2',
          'poll.enableMajorityClose': 'Cerrar por Umbral de Mayoría',
          'poll.enableMajorityCloseDesc': 'Cerrar encuesta cuando cualquier horario obtenga votos de mayoría',
          'poll.expectedParticipants': 'Total de Participantes Esperados',
          'poll.expectedParticipantsDesc': 'Número total de personas que se espera que voten (para cálculo de mayoría)',
          'poll.expectedParticipantsPlaceholder': 'ej. 2',

          // Poll closure
          'poll.closePoll': 'Cerrar Encuesta',
          'poll.closePollConfirm': '¿Estás seguro de que quieres cerrar esta encuesta?',
          'poll.closePollDesc': 'Esto evitará más votaciones y seleccionará un horario ganador.',
          'poll.selectWinningSlot': 'Seleccionar Horario Ganador',
          'poll.autoSelectWinner': 'Seleccionar automáticamente el más popular',
          'poll.manualSelectWinner': 'Seleccionar ganador manualmente',
          'poll.confirmClose': 'Confirmar Cierre',
          'poll.pollClosed': 'Encuesta Cerrada',
          'poll.closedBy': 'Cerrada por',
          'poll.closedReason': 'Razón',
          'poll.closedReasonManual': 'Cerrada manualmente',
          'poll.closedReasonDeadline': 'Fecha límite alcanzada',
          'poll.closedReasonParticipantLimit': 'Límite de participantes alcanzado',
          'poll.closedReasonMajority': 'Umbral de mayoría alcanzado',
          'poll.winningTimeSlot': 'Horario Ganador',
          'poll.reopenPoll': 'Reabrir Encuesta',
          'poll.activePolls': 'Encuestas Activas',


          // Home page
          'home.title': 'Programa Reuniones Sin Esfuerzo',
          'home.subtitle': 'Une los puntos. Reúnete en el momento perfecto.',
          'home.description': 'Crea encuestas, coordina con tu equipo y encuentra el momento perfecto para reunirse sin esfuerzo.',
          'home.getStarted': 'Comenzar',
          'home.learnMore': 'Saber Más',
          'home.features.title': 'Características Poderosas',
          'home.features.smartTimezone': 'Manejo Inteligente de Zonas Horarias',
          'home.features.smartTimezoneDesc': 'Convierte automáticamente los horarios a las zonas locales de los participantes',
          'home.features.anonymous': 'Votación Anónima',
          'home.features.anonymousDesc': 'Permite a los participantes votar sin crear una cuenta',
          'home.features.realtime': 'Actualizaciones en Tiempo Real',
          'home.features.realtimeDesc': 'Ve los resultados de votos actualizarse instantáneamente mientras las personas responden',
          'home.features.calendar': 'Integración de Calendario',
          'home.features.calendarDesc': 'Exporta los horarios finales de reunión directamente a tu calendario',
          'home.features.responsive': 'Amigable para Móviles',
          'home.features.responsiveDesc': 'Funciona perfectamente en todos los dispositivos y tamaños de pantalla',
          'home.features.analytics': 'Analíticas Inteligentes',
          'home.features.analyticsDesc': 'Obtén información sobre patrones de participación y horarios óptimos de reunión',
          'home.features.subtitle': 'Todo lo que necesitas para programar reuniones de manera eficiente y sin esfuerzo.',
          'home.features.allFeatures': 'Todas las Características',
          'home.cta.title': '¿Listo para optimizar tus reuniones?',
          'home.cta.subtitle': 'Únete a miles de equipos que usan Meet para coordinar sus horarios sin esfuerzo.',
          'home.cta.createFirstPoll': 'Crear Tu Primera Encuesta',

          // Meet section
          'home.meet.title': 'Une los puntos para reunirse en el momento perfecto',
          'home.meet.subtitle': 'Experimenta una programación fluida que une a los equipos sin esfuerzo.',
          'home.meet.startNow': 'Comenzar Ahora',
          'home.meet.exploreFeatures': 'Explorar Características',
          'home.meet.stats.polls': 'Encuestas Creadas',
          'home.meet.stats.participants': 'Participantes Satisfechos',
          'home.meet.stats.satisfaction': 'Tasa de Satisfacción',

          // How It Works section
          'home.howItWorks.title': 'Cómo Funciona',
          'home.howItWorks.subtitle': 'Pasos simples para coordinar reuniones perfectas',
          'home.howItWorks.step': 'Paso',
          'home.howItWorks.step1.title': 'Crea Tu Encuesta',
          'home.howItWorks.step1.description': 'Configura los detalles de tu reunión y sugiere horarios usando nuestra interfaz de calendario intuitiva.',
          'home.howItWorks.step2.title': 'Comparte el Enlace',
          'home.howItWorks.step2.description': 'Envía el enlace de la encuesta a los participantes por email, chat o cualquier plataforma de mensajería.',
          'home.howItWorks.step3.title': 'Recolecta Votos',
          'home.howItWorks.step3.description': 'Los participantes votan sobre su disponibilidad con opciones simples de sí/tal vez/no.',
          'home.howItWorks.step4.title': 'Revisa Resultados',
          'home.howItWorks.step4.description': 'Ve los horarios óptimos y finaliza tu reunión con la mejor asistencia.',
          'home.howItWorks.interactive.create': 'Haz clic para crear encuesta',
          'home.howItWorks.interactive.share': 'Copiar enlace para compartir',
          'home.howItWorks.interactive.vote': 'Votar en horarios',
          'home.howItWorks.interactive.results': 'Ver horarios óptimos',

          // How It Works - Demo Content
          'home.howItWorks.demo.teamMeetingPoll': 'Encuesta de Reunión de Equipo',
          'home.howItWorks.demo.pollTitle': 'Título de la Encuesta',
          'home.howItWorks.demo.weeklyTeamSync': 'Sincronización Semanal del Equipo',
          'home.howItWorks.demo.selectedTimeSlots': 'Horarios Seleccionados',
          'home.howItWorks.demo.monday': 'Lunes',
          'home.howItWorks.demo.wednesday': 'Miércoles',
          'home.howItWorks.demo.friday': 'Viernes',
          'home.howItWorks.demo.dec16': '16 Dic',
          'home.howItWorks.demo.dec18': '18 Dic',
          'home.howItWorks.demo.dec20': '20 Dic',
          'home.howItWorks.demo.time1': '2:00 PM - 3:00 PM',
          'home.howItWorks.demo.time2': '10:00 AM - 11:00 AM',
          'home.howItWorks.demo.time3': '3:00 PM - 4:00 PM',
          'home.howItWorks.demo.createPoll': 'Crear Encuesta',
          'home.howItWorks.demo.sharePoll': 'Compartir Encuesta',
          'home.howItWorks.demo.pollCreatedSuccess': '¡Encuesta creada exitosamente!',
          'home.howItWorks.demo.shareLink': 'Comparte este enlace con los participantes',
          'home.howItWorks.demo.quickShareOptions': 'Opciones de compartir rápido',
          'home.howItWorks.demo.email': 'Email',
          'home.howItWorks.demo.chat': 'Chat',
          'home.howItWorks.demo.pollId': 'ID de Encuesta: abc123xyz',
          'home.howItWorks.demo.timeSlotsAvailable': '3 horarios disponibles',
          'home.howItWorks.demo.anonymousVotingEnabled': 'Votación anónima habilitada',
          'home.howItWorks.demo.votingMatrix': 'Matriz de Votación',
          'home.howItWorks.demo.participant': 'Participante',
          'home.howItWorks.demo.mon2pm': 'Lun 2PM',
          'home.howItWorks.demo.wed10am': 'Mié 10AM',
          'home.howItWorks.demo.fri3pm': 'Vie 3PM',
          'home.howItWorks.demo.user1': 'Daniel Jiménez',
          'home.howItWorks.demo.user2': 'Samuel Santana',
          'home.howItWorks.demo.user3': 'Jose Pérez',
          'home.howItWorks.demo.you': 'Tú (Vanessa)',
          'home.howItWorks.demo.yes': 'Sí',
          'home.howItWorks.demo.maybe': 'Tal vez',
          'home.howItWorks.demo.no': 'No',
          'home.howItWorks.demo.pollResults': 'Resultados de la Encuesta',
          'home.howItWorks.demo.bestOption': 'Mejor Opción',
          'home.howItWorks.demo.mondayDec16': 'Lunes, 16 Dic',
          'home.howItWorks.demo.participantsAvailable': '4/4 participantes disponibles',
          'home.howItWorks.demo.allOptions': 'Todas las Opciones',
          'home.howItWorks.demo.monday2pm': 'Lunes 2:00 PM',
          'home.howItWorks.demo.wednesday10am': 'Miércoles 10:00 AM',
          'home.howItWorks.demo.friday3pm': 'Viernes 3:00 PM',
          'home.howItWorks.demo.best': 'Mejor',
          'home.howItWorks.demo.finalizeMeeting': 'Finalizar Reunión',

          // Testimonials section
          'home.testimonials.title': 'Lo Que Dicen Nuestros Usuarios',
          'home.testimonials.subtitle': 'Únete a miles de equipos satisfechos en todo el mundo',
          'home.testimonials.testimonial1.text': 'Meet ha revolucionado cómo programamos las reuniones del equipo. ¡No más cadenas infinitas de emails!',
          'home.testimonials.testimonial1.author': 'James Wilson',
          'home.testimonials.testimonial1.role': 'Gerente de Proyecto',
          'home.testimonials.testimonial2.text': 'El manejo de zonas horarias es increíble. Nuestro equipo global finalmente puede coordinarse sin esfuerzo.',
          'home.testimonials.testimonial2.author': 'Michael Chen',
          'home.testimonials.testimonial2.role': 'Líder de Ingeniería',
          'home.testimonials.testimonial3.text': 'Simple, intuitivo y poderoso. Meet nos ahorra horas cada semana.',
          'home.testimonials.testimonial3.author': 'Emma Rodriguez',
          'home.testimonials.testimonial3.role': 'Directora de Operaciones',
          'home.testimonials.testimonial4.text': 'La función de votación anónima es perfecta para nuestras reuniones con clientes. ¡Muy recomendado!',
          'home.testimonials.testimonial4.author': 'Lisa Park',
          'home.testimonials.testimonial4.role': 'Consultor',
          'home.testimonials.testimonial5.text': 'La mejor herramienta de programación que hemos usado. Interfaz limpia y excelentes características.',
          'home.testimonials.testimonial5.author': 'David Thompson',
          'home.testimonials.testimonial5.role': 'Gerente de Marketing',
          'home.testimonials.testimonial6.text': 'Meet hace que coordinar con socios externos sea mucho más fácil. ¡Me encanta la simplicidad!',
          'home.testimonials.testimonial6.author': 'Sarah Johnson',
          'home.testimonials.testimonial6.role': 'Desarrollo de Negocios',

          // Highlighted Features section
          'home.highlightedFeatures.title': 'Características Poderosas',
          'home.highlightedFeatures.subtitle': 'Descubre qué hace que nuestra plataforma de encuestas sea la elección perfecta para tu equipo',
          'home.highlightedFeatures.learnMore': 'Saber más',
          'home.highlightedFeatures.anonymousCreation.title': 'Creación de Encuestas Anónimas',
          'home.highlightedFeatures.anonymousCreation.description': 'Crea encuestas al instante sin necesidad de iniciar sesión. Comienza a coordinar reuniones en segundos, sin barreras ni fricciones de registro.',
          'home.highlightedFeatures.anonymousVoting.title': 'Votación Anónima',
          'home.highlightedFeatures.anonymousVoting.description': 'Los participantes pueden votar sobre su disponibilidad sin crear cuentas. Experiencia de votación simple, rápida y centrada en la privacidad.',
          'home.highlightedFeatures.analytics.title': 'Análisis Poderosos',
          'home.highlightedFeatures.analytics.description': 'Obtén información detallada sobre tus encuestas con análisis completos, gráficos y KPIs para tomar decisiones de programación basadas en datos.',
          'home.highlightedFeatures.emailEmbedding.title': 'Integración por Email',
          'home.highlightedFeatures.emailEmbedding.description': 'Integra encuestas directamente en emails con tablas HTML y enlaces de votación directa. Permite a los participantes votar sin salir de su bandeja de entrada.',

          // Dashboard
          'dashboard.totalPolls': 'Total de Encuestas',
          'dashboard.totalParticipants': 'Total de Participantes',
          'dashboard.activePolls': 'Encuestas Activas',
          'dashboard.completedPolls': 'Encuestas Completadas',
          'dashboard.viewAnalytics': 'Ver Analíticas',
          'dashboard.pollStatus.active': 'Activa',
          'dashboard.pollStatus.closed': 'Cerrada',
          'dashboard.loading': 'Cargando encuestas...',
          'dashboard.loadError': 'Error al cargar encuestas',
          'dashboard.welcomeBack': '¡Bienvenido de vuelta, {{name}}!',
          'dashboard.overviewDescription': 'Aquí tienes un resumen de tus encuestas de reuniones y actividad de programación.',
          'dashboard.recentPolls': 'Encuestas Recientes',
          'dashboard.viewAll': 'Ver Todas',
          'dashboard.createdPolls': 'Encuestas Creadas',
          'dashboard.noPollsYet': 'Aún no hay encuestas',
          'dashboard.createFirstPoll': 'Crea tu primera encuesta para comenzar a programar reuniones con tu equipo.',
          'dashboard.noDescription': 'Sin descripción',
          'dashboard.unknownDate': 'Fecha desconocida',
          'dashboard.timeSlots': 'franjas horarias',
          'dashboard.participants': 'participantes',
          'dashboard.editPoll': 'Editar Encuesta',
          'dashboard.viewPoll': 'Ver Encuesta',
          'dashboard.recentActivity': 'Actividad Reciente',
          'dashboard.noRecentActivity': 'Sin actividad reciente',
          'dashboard.recentActivityDescription': 'Tu actividad reciente de encuestas aparecerá aquí.',
          'dashboard.anonymousActivityDescription': 'El seguimiento de actividad está disponible para usuarios registrados. Crea una cuenta para ver tu actividad de encuestas.',
          'dashboard.activityLoading': 'Cargando actividad...',
          'dashboard.activity.pollCreated': 'creó la encuesta',
          'dashboard.activity.pollUpdated': 'actualizó la encuesta',
          'dashboard.activity.userVoted': 'votó en',
          'dashboard.activity.profileUpdated': 'actualizó el perfil',
          'dashboard.activity.pollRemoved': 'eliminó la encuesta',
          'dashboard.activity.pollClosed': 'cerró la encuesta',
          'dashboard.activity.pollReopened': 'reabrió la encuesta',
          'dashboard.activity.generalActivity': 'Actividad General',
          'dashboard.activity.justNow': 'ahora mismo',
          'dashboard.activity.minutesAgo': 'hace {{count}} minutos',
          'dashboard.activity.hoursAgo': 'hace {{count}} horas',
          'dashboard.activity.daysAgo': 'hace {{count}} días',
          'dashboard.activity.weeksAgo': 'hace {{count}} semanas',
          'common.retry': 'Reintentar',

          // Polls Management Page
          'polls.title': 'Mis Encuestas',
          'polls.description': 'Gestiona todas tus encuestas de reuniones en un solo lugar.',
          'polls.searchPlaceholder': 'Buscar encuestas...',
          'polls.filterAll': 'Todas las Encuestas',
          'polls.filterActive': 'Activas',
          'polls.filterClosed': 'Cerradas',
          'polls.table.name': 'Nombre de la Encuesta',
          'polls.table.status': 'Estado',
          'polls.table.participants': 'Participantes',
          'polls.table.selectedSlot': 'Horario Seleccionado',
          'polls.table.created': 'Creada',
          'polls.table.actions': 'Acciones',
          'polls.noPolls': 'No se encontraron encuestas',
          'polls.noPollsDesc': 'Crea tu primera encuesta para comenzar.',
          'polls.deleteConfirm': 'Eliminar Encuesta',
          'polls.deleteConfirmDesc': '¿Estás seguro de que quieres eliminar esta encuesta? Esta acción no se puede deshacer.',
          'polls.deleteSuccess': 'Encuesta eliminada exitosamente',
          'polls.deleteError': 'Error al eliminar la encuesta',
          'polls.sortBy': 'Ordenar por',
          'polls.itemsPerPage': 'Elementos por página',
          'polls.showing': 'Mostrando {{start}} a {{end}} de {{total}} encuestas',

          // Features page
          'features.subtitle': 'Descubre todas las características poderosas que hacen de Meet la mejor opción para programar reuniones y coordinar con tu equipo.',
          'features.smartTimezone.details': 'Detecta y convierte automáticamente las zonas horarias para todos los participantes, asegurando que todos vean los horarios en su zona horaria local sin confusión.',
          'features.anonymous.details': 'Los participantes pueden votar en encuestas sin crear una cuenta, facilitando obtener respuestas de partes interesadas externas y clientes.',
          'features.realtime.details': 'Ve votos y respuestas actualizarse en tiempo real mientras los participantes responden, impulsado por la tecnología de base de datos en tiempo real de Firebase.',
          'features.calendar.details': 'Una vez que se decide un horario, exporta la reunión directamente a Google Calendar, Outlook, o descarga como archivo ICS.',
          'features.responsive.details': 'Diseño completamente responsivo asegura una experiencia perfecta en escritorio, tablet y dispositivos móviles con interacciones táctiles amigables.',
          'features.smartScheduling.title': 'Programación Inteligente',
          'features.smartScheduling.description': 'Sugerencias inteligentes de franjas horarias basadas en horarios de trabajo y preferencias',
          'features.smartScheduling.details': 'Nuestro sistema aprende de patrones de uso para sugerir los horarios de reunión más probables de ser exitosos para tu equipo.',
          'features.privacy.title': 'Privacidad Primero',
          'features.privacy.description': 'Tus datos están seguros y privados con seguridad de nivel empresarial',
          'features.privacy.details': 'Todos los datos están encriptados en tránsito y en reposo, con controles de privacidad estrictos y cumplimiento GDPR.',
          'features.analytics.title': 'Análisis e Insights',
          'features.analytics.description': 'Obtén insights sobre los patrones de programación y preferencias de tu equipo',
          'features.analytics.details': 'Comprende cuándo tu equipo está más disponible y optimiza la programación futura de reuniones.',

          'features.cta.title': '¿Listo para experimentar el futuro de la programación de reuniones?',
          'features.cta.subtitle': 'Únete a miles de equipos que han optimizado su programación con las características inteligentes de Meet.',
          'features.cta.startCreating': 'Comenzar a Crear Encuestas',
          'features.cta.signUpFree': 'Registrarse Gratis',

          // Analytics
          'analytics.title': 'Panel de Análisis',
          'analytics.subtitle': 'Insights y tendencias para tus encuestas',
          'analytics.refresh': 'Actualizar Datos',
          'analytics.errorLoading': 'Error al cargar los datos de análisis',
          'analytics.noData': '¡Aún no hay datos de análisis disponibles. Crea algunas encuestas para ver insights!',

          // Analytics KPIs
          'analytics.kpis.totalPolls': 'Total de Encuestas',
          'analytics.kpis.totalParticipants': 'Total de Participantes',
          'analytics.kpis.avgParticipation': 'Participación Promedio',
          'analytics.kpis.avgResponseTime': 'Tiempo de Respuesta Promedio',
          'analytics.kpis.activePolls': 'Encuestas Activas',
          'analytics.kpis.completedPolls': 'Encuestas Completadas',

          // Analytics Charts
          'analytics.charts.pollsOverTime': 'Encuestas y Participación en el Tiempo',
          'analytics.charts.pollsOverTimeDesc': 'Rastrea las tendencias de creación de encuestas y participación',
          'analytics.charts.polls': 'Encuestas Creadas',
          'analytics.charts.participants': 'Participantes',
          'analytics.charts.totalPollsCreated': 'Total de Encuestas Creadas',
          'analytics.charts.totalParticipations': 'Total de Participaciones',

          'analytics.charts.participationRates': 'Tasas de Participación en Encuestas',
          'analytics.charts.participationRatesDesc': 'Ve qué encuestas obtienen más participación',
          'analytics.charts.participationRate': 'Tasa de Participación',
          'analytics.charts.highParticipation': 'Alta',
          'analytics.charts.mediumParticipation': 'Media',
          'analytics.charts.lowParticipation': 'Baja',
          'analytics.charts.veryLowParticipation': 'Muy Baja',
          'analytics.charts.averageParticipation': 'Participación Promedio',

          'analytics.charts.activityTimeline': 'Línea de Tiempo de Actividad',
          'analytics.charts.activityTimelineDesc': 'Monitorea tu actividad de encuestas en el tiempo',
          'analytics.charts.totalActivities': 'Total de Actividades',
          'analytics.charts.pollsCreated': 'Encuestas Creadas',
          'analytics.charts.votesReceived': 'Votos Recibidos',
          'analytics.charts.pollsUpdated': 'Encuestas Actualizadas',

          'analytics.charts.timeInsights': 'Preferencias de Tiempo e Insights',
          'analytics.charts.timeInsightsDesc': 'Entiende cuándo la gente prefiere reunirse',
          'analytics.charts.peakHours': 'Horas Pico',
          'analytics.charts.voteDistribution': 'Distribución de Votos',
          'analytics.charts.votes': 'votos',
          'analytics.charts.percentage': 'Porcentaje',
          'analytics.charts.mostActiveHour': 'Hora Más Activa',
          'analytics.charts.mostCommonResponse': 'Respuesta Más Común',

          // Analytics Insights
          'analytics.insights.title': 'Insights Clave',
          'analytics.insights.popularTime': 'Hora Más Popular',
          'analytics.insights.peakHour': 'Hora Pico de Actividad',
          'analytics.insights.avgDuration': 'Duración Promedio de Encuesta',
          'analytics.insights.topUser': 'Participante Principal',
          'analytics.insights.days': 'días',
          'analytics.insights.noData': 'Sin datos',

          // Footer
          'footer.description': 'La forma inteligente de programar reuniones con colaboración en tiempo real e integración perfecta de calendario.',
          'footer.copyright': '© {{year}} Meet. Todos los derechos reservados.',
          'footer.madeWith': 'Hecho con',
          'footer.forBetterMeetings': 'para mejores reuniones',
          'footer.product.title': 'Producto',
          'footer.product.pricing': 'Precios',
          'footer.product.api': 'API',
          'footer.product.integrations': 'Integraciones',
          'footer.company.title': 'Empresa',
          'footer.company.about': 'Acerca de',
          'footer.company.blog': 'Blog',
          'footer.company.careers': 'Carreras',
          'footer.company.contact': 'Contacto',
          'footer.support.title': 'Soporte',
          'footer.support.helpCenter': 'Centro de Ayuda',
          'footer.support.documentation': 'Documentación',
          'footer.support.status': 'Estado',
          'footer.support.community': 'Comunidad',
          'footer.legal.title': 'Legal',
          'footer.legal.privacy': 'Política de Privacidad',
          'footer.legal.terms': 'Términos de Servicio',
          'footer.legal.cookies': 'Política de Cookies',
          'footer.legal.gdpr': 'GDPR',

          // Help Center
          'help.title': 'Centro de Ayuda',
          'help.subtitle': 'Hola, ¿cómo podemos ayudarte?',
          'help.searchPlaceholder': 'Buscar cualquier cosa...',
          'help.popularTopics': 'Temas populares',
          'help.searchResults': 'Resultados de Búsqueda',
          'help.articles': 'Artículos',
          'help.learnMore': 'Saber más',
          'help.noResults': 'No se encontraron resultados',
          'help.noResultsDescription': 'Intenta ajustar tus términos de búsqueda o navega por nuestros temas populares a continuación.',
          'help.tableOfContents': 'En esta página',
          'help.lastUpdated': 'Última actualización: {{date}}',
          'help.relatedArticles': 'Artículos Relacionados',
          'help.categories.all': 'Todos',
          'help.categories.gettingStarted.title': 'Primeros Pasos',
          'help.categories.gettingStarted.description': 'Aprende lo básico de crear y gestionar encuestas',
          'help.categories.voting.title': 'Votación y Participantes',
          'help.categories.voting.description': 'Entiende cómo funciona la votación y gestiona participantes',
          'help.categories.features.title': 'Características y Configuración',
          'help.categories.features.description': 'Explora características avanzadas y opciones de personalización',

          // Help Voting
          'help.voting.question': '¿Te resultó útil este artículo?',
          'help.voting.yes': 'Sí',
          'help.voting.no': 'No',
          'help.voting.thankYou': '¡Gracias por tu comentario!',
          'help.voting.foundHelpful': 'Nos alegra que este artículo te haya sido útil.',
          'help.voting.foundNotHelpful': 'Trabajaremos en mejorar este contenido.',
          'help.voting.feedback': 'Tus comentarios nos ayudan a mejorar nuestra documentación.',

          // FAQ
          'help.faq.title': 'Preguntas frecuentes',
          'help.faq.createFirstPoll.question': '¿Cómo creo mi primera encuesta?',
          'help.faq.createFirstPoll.answer': 'Haz clic en "Crear Encuesta" en la barra de navegación, completa los detalles de tu encuesta, selecciona horarios usando nuestra interfaz de calendario y comparte el enlace con los participantes.',
          'help.faq.participantsJoin.question': '¿Cómo se unen los participantes a mi encuesta?',
          'help.faq.participantsJoin.answer': 'Comparte el enlace de la encuesta con los participantes. Pueden votar de forma anónima sin crear una cuenta, o iniciar sesión para una experiencia personalizada.',
          'help.faq.reuseQuestions.question': '¿Puedo reutilizar preguntas en diferentes encuestas?',
          'help.faq.reuseQuestions.answer': 'Actualmente, cada encuesta es independiente. Sin embargo, puedes copiar horarios y configuraciones de encuestas anteriores al crear nuevas.',
          'help.faq.viewResults.question': '¿Cómo veo los resultados de una encuesta?',
          'help.faq.viewResults.answer': 'Los resultados se muestran en tiempo real en la página de la encuesta. Puedes ver conteos de votos, nombres de participantes y exportar el horario final a tu calendario.',
          'help.faq.customizeAppearance.question': '¿Puedo personalizar la apariencia de mis encuestas?',
          'help.faq.customizeAppearance.answer': '¡Sí! Puedes elegir entre temas claro y oscuro, y la interfaz se adapta automáticamente a tus preferencias.',

          // Help Articles
          'help.articles.creatingFirstPoll.title': 'Creando Tu Primera Encuesta',
          'help.articles.creatingFirstPoll.welcome.title': 'Bienvenido a Meet',
          'help.articles.creatingFirstPoll.welcome.content': 'Aprende lo básico de configurar tu cuenta y crear tu primera encuesta de aprendizaje interactivo en nuestra plataforma.',
          'help.articles.creatingFirstPoll.accountSetup.title': 'Configurando Tu Cuenta',
          'help.articles.creatingFirstPoll.accountSetup.content': 'Antes de poder comenzar a crear encuestas, necesitarás configurar tu cuenta con tu información básica y preferencias.',
          'help.articles.creatingFirstPoll.accountSetup.signup.title': 'Proceso de Registro',
          'help.articles.creatingFirstPoll.accountSetup.signup.content': 'Completa tu perfil agregando tu nombre y foto de perfil en la sección de Perfil.\nEstablece tu idioma preferido en la página de Configuración.\nAgrega los detalles de tu organización o equipo en la página de Configuración.\nConfigura los ajustes de tu banco de encuestas para determinar cómo se guardan y reutilizan las preguntas.',
          'help.articles.creatingFirstPoll.accountSetup.anonymous.title': 'Opción Anónima',
          'help.articles.creatingFirstPoll.accountSetup.anonymous.content': 'También puedes crear encuestas sin registrarte usando nuestro modo anónimo. Simplemente haz clic en "Crear Encuesta" y comienza a construir tu encuesta inmediatamente.',
          'help.articles.creatingFirstPoll.creating.title': 'Creando Tu Primera Encuesta',
          'help.articles.creatingFirstPoll.creating.content': 'Crear una encuesta es simple y directo. Sigue estos pasos para crear tu primera encuesta de aprendizaje interactivo.',
          'help.articles.creatingFirstPoll.creating.basicInfo.title': 'Información Básica',
          'help.articles.creatingFirstPoll.creating.basicInfo.content': 'Entra al Panel, haz clic en "Nueva Encuesta", y selecciona el tipo de encuesta que quieres crear: Libro, Equipo, o Sin conexión.\nAgrega preguntas a tu encuesta: Puedes crear preguntas de opción múltiple, verdadero/falso, y otros tipos de preguntas, o generar preguntas usando IA.\nAgrega preguntas usando IA.',
          'help.articles.creatingFirstPoll.creating.timeSlots.title': 'Horarios',
          'help.articles.creatingFirstPoll.creating.timeSlots.content': 'Configura ajustes adicionales como límites de tiempo, protección con contraseña, y guardado automático de preguntas.\nHaz clic en "Crear Encuesta" para finalizar y guardar tu encuesta.',
          'help.articles.creatingFirstPoll.sharing.title': 'Compartiendo Tu Encuesta',
          'help.articles.creatingFirstPoll.sharing.content': 'Después de crear tu encuesta, necesitarás invitar participantes a unirse. Comparte este código con tus participantes a través de email, aplicaciones de mensajería, o mostrándolo en una pantalla.\nPara mayor seguridad, puedes habilitar protección con contraseña para tu encuesta.\nTambién puedes descargar reportes para análisis sin conexión.',

          'help.articles.pollSettings.title': 'Guía de Configuración de Encuestas',
          'help.articles.pollSettings.overview.title': 'Resumen de Configuración',
          'help.articles.pollSettings.overview.content': 'La configuración de encuestas te permite personalizar cómo se comporta y aparece tu encuesta a los participantes.',
          'help.articles.pollSettings.basic.title': 'Configuración Básica',
          'help.articles.pollSettings.basic.content': 'Configura opciones básicas de encuesta como título, descripción y horarios.',
          'help.articles.pollSettings.advanced.title': 'Configuración Avanzada',
          'help.articles.pollSettings.advanced.content': 'Configura características avanzadas como múltiples votos, fechas límite y restricciones de participantes.',

          'help.articles.voting.title': 'Guía de Votación',
          'help.articles.voting.howTo.title': 'Cómo Votar',
          'help.articles.voting.howTo.content': 'Los participantes pueden votar haciendo clic en sus horarios preferidos. La interfaz es intuitiva y amigable para móviles.',
          'help.articles.voting.options.title': 'Opciones de Voto',
          'help.articles.voting.options.content': 'Elige entre Sí (verde), Tal vez (naranja), o No (rojo) para cada horario.',
          'help.articles.voting.anonymous.title': 'Votación Anónima',
          'help.articles.voting.anonymous.content': 'Los participantes pueden votar sin crear una cuenta, facilitando la participación de partes interesadas externas.',

          'help.articles.participants.title': 'Gestionando Participantes',
          'help.articles.participants.inviting.title': 'Invitando Participantes',
          'help.articles.participants.inviting.content': 'Comparte el enlace de la encuesta con participantes vía email, mensajería o redes sociales.',
          'help.articles.participants.management.title': 'Gestión de Participantes',
          'help.articles.participants.management.content': 'Ve las respuestas de participantes en tiempo real y gestiona su acceso a la encuesta.',

          'help.articles.calendar.title': 'Exportar Calendario',
          'help.articles.calendar.export.title': 'Opciones de Exportación',
          'help.articles.calendar.export.content': 'Exporta tus horarios de reunión finalizados directamente a tu aplicación de calendario.',
          'help.articles.calendar.formats.title': 'Formatos Soportados',
          'help.articles.calendar.formats.content': 'Soportamos Google Calendar, Outlook, y formatos de archivo ICS para máxima compatibilidad.',

          'help.articles.timezone.title': 'Soporte de Zona Horaria',
          'help.articles.timezone.howItWorks.title': 'Cómo Funciona',
          'help.articles.timezone.howItWorks.content': 'Nuestro sistema detecta y convierte automáticamente las zonas horarias para todos los participantes.',
          'help.articles.timezone.setting.title': 'Configurando Tu Zona Horaria',
          'help.articles.timezone.setting.content': 'Tu zona horaria se detecta automáticamente, pero puedes ajustarla manualmente en la configuración de tu perfil.',

          // New Help Articles
          'help.articles.emailEmbedding.title': 'Integración de Encuestas por Email',
          'help.articles.emailEmbedding.overview.title': 'Resumen',
          'help.articles.emailEmbedding.overview.content': 'La integración por email te permite incluir tu encuesta directamente en mensajes de correo electrónico, facilitando que los participantes voten sin salir de su bandeja de entrada. Esta función soporta tablas HTML con CSS en línea y enlaces de votación directa.',
          'help.articles.emailEmbedding.howTo.title': 'Cómo Integrar Encuestas en Emails',
          'help.articles.emailEmbedding.howTo.content': 'Para integrar una encuesta en un email:\n\n1. Abre tu encuesta y haz clic en el botón "Compartir"\n2. Selecciona "Integración por Email" de las opciones de compartir\n3. Elige tu formato de integración preferido (tabla HTML o enlaces simples)\n4. Copia el código HTML generado\n5. Pégalo en tu cliente de email que soporte HTML\n6. Envía el email a tus participantes\n\nLos participantes pueden hacer clic directamente en los horarios para votar, y sus votos se registrarán automáticamente.',
          'help.articles.emailEmbedding.htmlCode.title': 'Código HTML y Estilos',
          'help.articles.emailEmbedding.htmlCode.content': 'El código HTML generado usa CSS en línea para asegurar compatibilidad entre diferentes clientes de email. La encuesta integrada incluye:\n\n• Una tabla formateada mostrando todos los horarios disponibles\n• Enlaces de votación directa para cada horario\n• Diseño responsivo que funciona en dispositivos móviles\n• Estilos consistentes que coinciden con el tema de tu encuesta\n\nPuedes personalizar la apariencia modificando los estilos CSS en línea antes de enviar.',
          'help.articles.emailEmbedding.directVoting.title': 'Enlaces de Votación Directa',
          'help.articles.emailEmbedding.directVoting.content': 'Cada horario en la encuesta integrada incluye un enlace de votación directa que:\n\n• Lleva a los participantes directamente a la interfaz de votación\n• Pre-selecciona el horario en el que hicieron clic\n• Funciona con votación anónima (no requiere inicio de sesión)\n• Rastrea votos en tiempo real\n• Proporciona retroalimentación inmediata después de votar\n\nEsta integración perfecta elimina la necesidad de que los participantes naveguen por múltiples páginas para emitir sus votos.',

          'help.articles.analytics.title': 'Panel de Análisis',
          'help.articles.analytics.overview.title': 'Resumen',
          'help.articles.analytics.overview.content': 'El panel de análisis proporciona información completa sobre tus encuestas, ayudándote a entender patrones de participación, tendencias de votación y compromiso general. Accede a los análisis haciendo clic en "Ver Análisis" desde tu panel principal o páginas de encuestas individuales.',
          'help.articles.analytics.kpis.title': 'Indicadores Clave de Rendimiento (KPIs)',
          'help.articles.analytics.kpis.content': 'El panel de análisis muestra varios KPIs importantes:\n\n• Número total de encuestas creadas\n• Total de participantes en todas las encuestas\n• Proporción de encuestas activas vs. completadas\n• Tiempo promedio de respuesta\n• Porcentaje de tasa de participación\n• Horarios más populares\n\nEstas métricas te ayudan a entender qué tan efectivamente estás coordinando reuniones e identificar áreas de mejora.',
          'help.articles.analytics.charts.title': 'Gráficos y Visualizaciones',
          'help.articles.analytics.charts.content': 'Las representaciones visuales de tus datos incluyen:\n\n• Tendencias de participación a lo largo del tiempo\n• Distribución de votos entre horarios\n• Patrones de respuesta por día de la semana\n• Tasas de finalización de encuestas\n• Distribución geográfica de participantes (cuando esté disponible)\n\nTodos los gráficos son interactivos y pueden filtrarse por rango de fechas, tipo de encuesta u otros criterios.',
          'help.articles.analytics.insights.title': 'Información Accionable',
          'help.articles.analytics.insights.content': 'El sistema de análisis proporciona información accionable como:\n\n• Horarios óptimos de reunión basados en datos históricos\n• Recomendaciones de compromiso de participantes\n• Mejores prácticas para creación de encuestas\n• Sugerencias para mejorar tasas de respuesta\n\nUsa esta información para optimizar tu proceso de programación de reuniones y aumentar la satisfacción de los participantes.',

          'help.articles.accountMatching.title': 'Coincidencia de Cuentas Anónimas',
          'help.articles.accountMatching.overview.title': 'Resumen',
          'help.articles.accountMatching.overview.content': 'Nuestra plataforma permite a usuarios anónimos crear encuestas y votar sin crear una cuenta. Más tarde, si deciden crear una cuenta (Gmail o email), sus datos de encuestas existentes y votos pueden preservarse y migrarse a su nueva cuenta.',
          'help.articles.accountMatching.howItWorks.title': 'Cómo Funciona la Coincidencia de Cuentas',
          'help.articles.accountMatching.howItWorks.content': 'Cuando un usuario anónimo crea una cuenta:\n\n1. El sistema identifica encuestas y votos asociados con su sesión de navegador\n2. Durante la creación de cuenta, se solicita a los usuarios reclamar sus datos anónimos\n3. Todas las encuestas, votos y preferencias se transfieren a la nueva cuenta\n4. El usuario mantiene acceso a toda su actividad previa\n5. La actividad futura se vincula automáticamente a su cuenta\n\nEste proceso es fluido y asegura que no se pierdan datos durante la transición.',
          'help.articles.accountMatching.dataPreservation.title': 'Preservación de Datos',
          'help.articles.accountMatching.dataPreservation.content': 'Los siguientes datos se preservan durante la migración de cuenta:\n\n• Todas las encuestas creadas como usuario anónimo\n• Historial de votación y preferencias\n• Avatares personalizados y nombres para mostrar\n• Configuraciones y ajustes de encuestas\n• Interacciones de participantes y comentarios\n\nTu actividad anónima se convierte en parte de tu historial de cuenta permanente, permitiendo análisis e información continua.',
          'help.articles.accountMatching.migration.title': 'Proceso de Migración',
          'help.articles.accountMatching.migration.content': 'El proceso de migración es automático y seguro:\n\n1. Crea tu cuenta usando Gmail o registro por email\n2. Confirma tu dirección de email si usas registro por email\n3. El sistema detectará y ofrecerá migrar tus datos anónimos\n4. Acepta la migración para transferir todos tus datos\n5. Tu cuenta ahora está completamente configurada con tus datos históricos\n\nSi encuentras algún problema durante la migración, contacta a nuestro equipo de soporte para asistencia.',

          // Poll Status Management Help Article - Spanish
          'help.articles.pollStatus.title': 'Gestión de Estado de Encuestas',
          'help.articles.pollStatus.overview.title': 'Resumen',
          'help.articles.pollStatus.overview.content': 'Entender cómo gestionar los estados de las encuestas es esencial para una coordinación efectiva de reuniones. Esta guía cubre los diferentes estados de encuestas, cómo cerrar y reabrir encuestas, y las diversas condiciones de cierre automático disponibles.',

          'help.articles.pollStatus.statuses.title': 'Estados de Encuestas',
          'help.articles.pollStatus.statuses.content': 'Las encuestas pueden tener dos estados principales: Activa y Cerrada. Cada estado determina qué acciones pueden tomar los participantes y cómo se comporta la encuesta.',

          'help.articles.pollStatus.statuses.active.title': 'Estado Activo',
          'help.articles.pollStatus.statuses.active.content': 'Cuando una encuesta está activa, los participantes pueden votar, cambiar sus votos, y nuevos participantes pueden unirse. El creador de la encuesta puede editar detalles, agregar o quitar horarios, y gestionar configuraciones. Las encuestas activas muestran actualizaciones en tiempo real mientras se emiten los votos.',

          'help.articles.pollStatus.statuses.closed.title': 'Estado Cerrado',
          'help.articles.pollStatus.statuses.closed.content': 'Cuando una encuesta está cerrada, no se pueden emitir nuevos votos y los votos existentes no pueden modificarse. Los resultados están finalizados y los participantes solo pueden ver el resultado final. Las encuestas cerradas muestran el horario ganador y la razón del cierre.',

          'help.articles.pollStatus.closing.title': 'Cerrar Encuestas',
          'help.articles.pollStatus.closing.content': 'Las encuestas pueden cerrarse manualmente por el creador o automáticamente basándose en condiciones predefinidas. Al cerrar una encuesta, puedes opcionalmente seleccionar un horario ganador.',

          'help.articles.pollStatus.closing.manual.title': 'Cierre Manual',
          'help.articles.pollStatus.closing.manual.content': 'Para cerrar manualmente una encuesta, navega a tu encuesta y haz clic en el botón "Cerrar Encuesta". Puedes elegir seleccionar automáticamente el horario más popular o seleccionar manualmente un horario específico como ganador. Una vez cerrada, los participantes verán los resultados finales.',

          'help.articles.pollStatus.closing.automatic.title': 'Cierre Automático',
          'help.articles.pollStatus.closing.automatic.content': 'Las encuestas pueden configurarse para cerrarse automáticamente basándose en tres condiciones: fecha límite alcanzada, límite de participantes alcanzado, o umbral de mayoría logrado. Estas configuraciones pueden ajustarse al crear o editar una encuesta en la sección "Configuraciones de Auto-cierre".',

          'help.articles.pollStatus.reopening.title': 'Reabrir Encuestas',
          'help.articles.pollStatus.reopening.content': 'Los creadores de encuestas pueden reabrir encuestas cerradas en cualquier momento haciendo clic en el botón "Reabrir Encuesta". Al reabrir una encuesta que fue cerrada automáticamente, el sistema eliminará o ajustará la condición que causó el cierre para prevenir un re-cierre inmediato. Por ejemplo, si una encuesta fue cerrada por una fecha límite, la fecha límite será eliminada al reabrir.',

          'help.articles.pollStatus.reasons.title': 'Razones de Cierre',
          'help.articles.pollStatus.reasons.content': 'Cuando una encuesta se cierra, el sistema registra la razón del cierre. Esto ayuda a los participantes a entender por qué terminó la votación y proporciona transparencia en el proceso de toma de decisiones.',

          'help.articles.pollStatus.reasons.manual.title': 'Cierre Manual',
          'help.articles.pollStatus.reasons.manual.content': 'El creador de la encuesta decidió manualmente cerrar la encuesta. Esto típicamente sucede cuando el creador ha reunido suficientes respuestas o quiere finalizar el horario de la reunión.',

          'help.articles.pollStatus.reasons.deadline.title': 'Fecha Límite Alcanzada',
          'help.articles.pollStatus.reasons.deadline.content': 'La encuesta fue cerrada automáticamente porque se alcanzó la fecha límite de votación. Esto asegura que la planificación de reuniones se mantenga en horario y previene períodos de votación indefinidos.',

          'help.articles.pollStatus.reasons.participantLimit.title': 'Límite de Participantes Alcanzado',
          'help.articles.pollStatus.reasons.participantLimit.content': 'La encuesta fue cerrada automáticamente porque se alcanzó el número máximo de participantes. Esto es útil para reuniones con capacidad limitada o cuando quieres cerrar la votación después de un número específico de respuestas.',

          'help.articles.pollStatus.reasons.majority.title': 'Umbral de Mayoría Logrado',
          'help.articles.pollStatus.reasons.majority.content': 'La encuesta fue cerrada automáticamente porque una mayoría de participantes esperados votó por el mismo horario. Esto permite una toma de decisiones rápida cuando hay consenso claro entre los participantes.',

          // Profile page
          'profile.title': 'Configuración de Perfil',
          'profile.subtitle': 'Gestiona la configuración y preferencias de tu cuenta',
          'profile.personalInfo': 'Información Personal',
          'profile.personalInfoDesc': 'Actualiza tu nombre para mostrar y avatar',
          'profile.displayName': 'Nombre para Mostrar',
          'profile.displayNamePlaceholder': 'Ingresa tu nombre para mostrar',
          'profile.googleDisplayNameNote': 'Puedes personalizar tu nombre para mostrar aquí. Tu nombre de cuenta de Google permanecerá sin cambios.',
          'profile.avatar': 'Avatar',
          'profile.avatarDesc': 'Elige un avatar para representarte',
          'profile.googleAvatarDesc': 'Usando tu foto de perfil de Google. Haz clic en actualizar para elegir un avatar personalizado.',
          'profile.refreshAvatar': 'Actualizar Avatar',
          'profile.uploadOwnImage': 'Subir Imagen Propia',
          'profile.avatarUpdated': 'Avatar actualizado exitosamente',
          'profile.randomAvatar': 'Avatar Aleatorio',
          'profile.useGoogleAvatar': 'Usar Avatar de Google',
          'profile.authMethod': 'Método de Autenticación',
          'profile.authMethodDesc': 'Cómo inicias sesión en tu cuenta',
          'profile.accountSecurity': 'Seguridad de la Cuenta',
          'profile.accountSecurityDesc': 'Cambia tu contraseña y configuración de seguridad',
          'profile.currentPassword': 'Contraseña Actual',
          'profile.newPassword': 'Nueva Contraseña',
          'profile.confirmNewPassword': 'Confirmar Nueva Contraseña',
          'profile.passwordRequirements': 'La contraseña debe tener al menos 6 caracteres',
          'profile.passwordStrength': 'Fortaleza de la Contraseña',
          'profile.passwordStrengthWeak': 'Débil',
          'profile.passwordStrengthMedium': 'Media',
          'profile.passwordStrengthStrong': 'Fuerte',
          'profile.changePassword': 'Cambiar Contraseña',
          'profile.googleManaged': 'Cuenta de Google',
          'profile.googleManagedDesc': 'Tu email y contraseña son gestionados por Google',
          'profile.anonymousAccount': 'Cuenta Anónima',
          'profile.anonymousAccountDesc': 'Estás usando una cuenta anónima',
          'profile.upgradeAccount': 'Actualizar Cuenta',
          'profile.linkToGoogle': 'Vincular con Google',
          'profile.createEmailAccount': 'Crear Cuenta de Email',
          'profile.saveChanges': 'Guardar Cambios',
          'profile.unsavedChanges': 'Cambios No Guardados',
          'profile.unsavedChangesDesc': 'Tienes cambios no guardados. ¿Estás seguro de que quieres salir?',
          'profile.discardChanges': 'Descartar Cambios',
          'profile.profileUpdated': 'Perfil actualizado exitosamente',
          'profile.passwordChanged': 'Contraseña cambiada exitosamente',
          'profile.displayNameRequired': 'El nombre para mostrar es requerido',
          'profile.displayNameTooShort': 'El nombre para mostrar debe tener al menos 2 caracteres',
          'profile.currentPasswordRequired': 'La contraseña actual es requerida',
          'profile.newPasswordRequired': 'La nueva contraseña es requerida',
          'profile.confirmPasswordRequired': 'Por favor confirma tu nueva contraseña',
          'profile.passwordsDoNotMatch': 'Las contraseñas no coinciden',
          'profile.currentPasswordIncorrect': 'La contraseña actual es incorrecta',
          'profile.emailPlaceholder': 'Ingresa tu email',
          'profile.currentPasswordPlaceholder': 'Ingresa tu contraseña actual',
          'profile.newPasswordPlaceholder': 'Ingresa tu nueva contraseña',
          'profile.confirmNewPasswordPlaceholder': 'Confirma tu nueva contraseña',
          'profile.emailAccount': 'Cuenta de Email',
          'profile.dataMigrationNote': 'Tus encuestas y datos serán transferidos a tu nueva cuenta.',
        },
      },
    },
  });
